// Add at the top of the file
const DEFAULT_COLORS = {
  primary: '#4F46E5',
  secondary: '#4B5563',
  accent: '#4B5563'
};

const DEFAULT_FONTS = {
  primary: 'Inter',
  secondary: 'Inter'
};

// At the top of the file, add these default theme values
const DEFAULT_THEME = {
  colors: {
    primary: '#4F46E5',
    accent: '#4B5563',
    background: {
      gradient: {
        start: '#F3F4F6',
        end: '#E5E7EB'
      }
    },
    cardBackground: '#FFFFFF',
    cardOpacity: 0.9,
    titleText: '#1F2937',
    subtitleText: '#4B5563',
    buttonBackground: '#4F46E5',
    buttonText: '#FFFFFF',
    headlineText: '#1F2937',
    subheadlineText: '#4B5563',
    results: {
      cardBackground: '#FFFFFF',
      cardBackgroundOpacity: 0.9,
      scoreLabelText: '#4B5563',
      scoreValueText: '#1F2937',
      joinButtonGradientFrom: '#4F46E5',
      joinButtonGradientTo: '#7C3AED',
      joinButtonText: '#FFFFFF'
    },
    endCard: {
      background: '#FFFFFF',
      backgroundOpacity: 0.9,
      playAgainBackground: '#4F46E5',
      playAgainText: '#FFFFFF',
      shareBackground: '#4B5563',
      shareText: '#FFFFFF',
      ctaButtonBackground: '#7C3AED',
      ctaButtonText: '#FFFFFF',
      ctaButtonOpacity: 1
    },
    leaderboard: {
      background: '#FFFFFF',
      backgroundOpacity: 0.9,
      leaderboardTitleText: '#1F2937',
      rankText: '#6B7280',
      nameText: '#111827',
      scoreText: '#4F46E5',
      userBackground: '#E5E7EB',
      tabPrimary: '#4F46E5',
      tabSecondary: '#6B7280',
      joinButton: {
        background: '#4F46E5',
        text: '#FFFFFF',
        opacity: 1
      },
      joinButtonGradientFrom: '#4F46E5',
      joinButtonGradientTo: '#7C3AED'
    }
  },
  typography: {
    primaryFont: 'Inter',
    secondaryFont: 'Inter',
    titleFont: 'Inter',
    subtitleFont: 'Inter',
    scoreFont: 'Inter',
    scoreValueFont: 'Inter',
    leaderboardTitleFont: 'Inter',
    rankFont: 'Inter',
    nameFont: 'Inter',
    joinButtonFont: 'Inter',
    ctaButtonFont: 'Inter'
  }
};

// Color utility functions
const hexToRgb = (hex) => {
  hex = hex.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  return { r, g, b };
};

// Function to generate custom theme from organized color structure and a font
export const generateCustomTheme = (colors, font, gameType) => {
  try {
    const { background, surface, primary, action, text } = colors;
    
    // Generate derived colors based on the organized structure (like retro theme)
    const backgroundDark = darkenColor(background, 0.8);  // For gradient end
    const actionDark = darkenColor(action, 0.8);         // For button hover states
    const surfaceLight = lightenColor(surface, 1.2);     // For subtle variations
    
    // Generate background gradient using background color (like retro: #2C3E50 -> #1A2530)
    const backgroundStart = background;
    const backgroundEnd = backgroundDark;
    
    // Use the organized colors directly (like retro theme structure)
    const cardBackground = surface;      // Like retro uses #34495E for cards
    const titleColor = primary;          // Like retro uses #F1C40F for titles  
    const subtitleColor = text;          // Like retro uses #ECF0F1 for text
    const buttonBackground = action;     // Like retro uses #E74C3C for buttons
    
    // Ensure text has good contrast on button
    const buttonTextColor = getContrastRatio(action, '#FFFFFF') > 4.5 ? '#FFFFFF' : '#000000';
    
    const customTheme = {
      colors: {
        primary: primary,
        accent: action,
        background: {
          gradient: {
            start: backgroundStart,
            end: backgroundEnd
          }
        },
        cardBackground: cardBackground,
        cardOpacity: 0.95,
        titleText: titleColor,
        subtitleText: subtitleColor,
        buttonBackground: buttonBackground,
        buttonOpacity: 1,
        buttonText: buttonTextColor,
        buttonBorder: actionDark,
        searchButtonBackground: buttonBackground,
        searchButtonText: buttonTextColor,
        searchButtonBorder: actionDark,
        headlineText: titleColor,
        subheadlineText: subtitleColor,
        results: {
          cardBackground: cardBackground,
          cardBackgroundOpacity: 0.95,
          scoreLabelText: subtitleColor,
          scoreValueText: titleColor,
          joinButtonGradientFrom: buttonBackground,
          joinButtonGradientTo: actionDark,
          joinButtonText: buttonTextColor
        },
        quizResults: {
          questionText: titleColor,
          answerText: subtitleColor,
          showAnswerText: subtitleColor
        },
        endCard: {
          background: cardBackground,
          backgroundOpacity: 0.95,
          playAgainBackground: buttonBackground,
          playAgainText: buttonTextColor,
          shareBackground: primary,
          shareText: getContrastRatio(primary, '#FFFFFF') > 4.5 ? '#FFFFFF' : '#000000',
          ctaButtonBackground: lightenColor(action, 1.1),
          ctaButtonText: buttonTextColor,
          ctaButtonOpacity: 1
        },
        leaderboard: {
          background: cardBackground,
          backgroundOpacity: 0.95,
          leaderboardTitleText: titleColor,
          rankText: subtitleColor,
          nameText: text,
          scoreText: primary,
          userBackground: backgroundDark,
          tabPrimary: buttonBackground,
          tabSecondary: subtitleColor,
          joinButtonGradientFrom: buttonBackground,
          joinButtonGradientTo: actionDark,
          joinButtonText: buttonTextColor
        }
      },
      typography: {
        primaryFont: font || 'Inter',
        secondaryFont: font || 'Inter',
        titleFont: font || 'Inter',
        subtitleFont: font || 'Inter',
        scoreFont: font || 'Inter',
        scoreValueFont: font || 'Inter',
        leaderboardTitleFont: font || 'Inter',
        rankFont: font || 'Inter',
        nameFont: font || 'Inter',
        joinButtonFont: font || 'Inter',
        ctaButtonFont: font || 'Inter',
        fontWeight: 'normal'
      }
    };

    // Add game-specific styles if gameType is provided (using new organized color structure)
    if (gameType) {
      if (gameType === 'snake') {
        customTheme.colors.game = {
          canvasColor: surface,
          snakeColor: primary,
          foodColor: action,
          controlButtonColor: surface,
          controlButtonOpacity: 1,
          controlIconColor: text,
          scoreText: primary
        };
      } else if (gameType === 'listmaster') {
        customTheme.colors.game = {
          headerBackground: background,
          headerBackgroundOpacity: 1,
          headerText: primary,
          headerBorder: surface,
          guessesText: text,
          guessesOpacity: 1,
          surfaceBackground: surface,
          surfaceBackgroundOpacity: 1,
          filledSlotBackground: action,
          filledSlotBackgroundOpacity: 1,
          filledSlotTextColor: getContrastRatio(action, '#FFFFFF') > 4.5 ? '#FFFFFF' : '#000000',
          text: text,
          border: backgroundDark,
          rankNumberColor: action,
          rankNumberOpacity: 1,
          contextText: text,
          contextOpacity: 1,
          inputBackground: surface,
          inputBackgroundOpacity: 1,
          inputText: text,
          inputBorder: surfaceLight,
          inputPlaceholder: text,
          keyboardBackground: backgroundDark,
          keyboardOpacity: 1,
          keyboardButtonBackground: surface,
          keyboardButtonOpacity: 1,
          keyboardText: text,
          keyboardBorder: surfaceLight,
          searchButtonBackground: action,
          searchButtonText: buttonTextColor,
          searchButtonBorder: actionDark,
          checkingColor: action,
          successColor: primary
        };
      } else if (gameType === 'cubefield') {
        customTheme.colors.game = {
          backgroundColor: background,
          playerColor: action,
          cubeColor: primary,
          gridColor: surface,
          scoreBackground: surface,
          scoreBackgroundOpacity: 0.95,
          scoreText: primary,
          controlButtonColor: surface,
          controlButtonOpacity: 1,
          controlIconColor: primary
        };
      } else if (gameType === 'hint') {
        customTheme.colors.game = {
          hintBackground: background,
          hintBackgroundOpacity: 1,
          hintText: primary,
          hintBorder: primary,
          hintIconColor: primary,
          inputBackground: surface,
          inputBackgroundOpacity: 1,
          inputText: text,
          inputBorder: surfaceLight,
          inputPlaceholder: text,
          resultBackground: surface,
          resultBackgroundOpacity: 1,
          resultText: text,
          resultHoverBackground: backgroundDark,
          keyboardBackground: backgroundDark,
          keyboardOpacity: 1,
          keyboardButtonBackground: surface,
          keyboardButtonOpacity: 1,
          keyboardText: text,
          keyboardBorder: surfaceLight,
          skipButtonBackground: action,
          skipButtonBackgroundOpacity: 0.2,
          skipButtonText: action,
          skipButtonHoverBackground: `${action}40`
        };
      } else if (gameType === 'mapselect') {
        customTheme.colors.game = {
          inputBackground: surface,
          inputBackgroundOpacity: 1,
          searchButtonBackground: action,
          searchButtonOpacity: 1,
          searchButtonBorder: surfaceLight,
          searchButtonText: buttonTextColor,
          inputText: text,
          inputBorder: surfaceLight,
          inputPlaceholder: text,
          resultsBackground: surface,
          resultsBackgroundOpacity: 1,
          resultsText: text,
          resultsHoverBackground: backgroundDark
        };
      } else if (gameType === 'quiz') {
        customTheme.colors.game = {
          surface: {
            background: surface,
            opacity: 1,
            questionText: text,
            questionBadgeBackground: action,
            questionBadgeOpacity: 0.2,
            questionBadgeText: action,
            answerBackground: backgroundDark,
            answerOpacity: 1,
            answerText: text,
            hintText: primary,
            hintBackground: background,
            hintBackgroundOpacity: 1,
            hintBorder: primary,
            hintIconColor: primary
          }
        };
      } else if (gameType === 'pindrop') {
        customTheme.colors.game = {
          topSection: background,
          textColor: primary,
          topSectionOpacity: 1,
          polyline: primary,
          marker: action
        };
      }
    }

    return customTheme;
  } catch (error) {
    console.error('Error generating custom theme:', error);
    return {
      colors: {
        primary: colors.primary || '#F1C40F',
        accent: colors.action || '#E74C3C',
        background: { gradient: { start: colors.background || '#2C3E50', end: colors.background || '#2C3E50' } },
        cardBackground: colors.surface || '#34495E',
        titleText: colors.primary || '#F1C40F',
        subtitleText: colors.text || '#ECF0F1',
        buttonBackground: colors.action || '#E74C3C',
        buttonText: '#FFFFFF'
      },
      typography: {
        primaryFont: font || 'Inter',
        fontWeight: 'normal'
      }
    };
  }
};

// Helper function to lighten colors
const lightenColor = (hex, factor = 1.2) => {
  try {
    const { r, g, b } = hexToRgb(hex);
    const hsl = rgbToHsl(r, g, b);
    hsl.l = Math.min(1, hsl.l * factor);
    const rgb = hslToRgb(hsl.h, hsl.s, hsl.l);
    return rgbToHex(rgb.r, rgb.g, rgb.b);
  } catch (error) {
    console.error('Error lightening color:', error);
    return hex;
  }
};

const rgbToHex = (r, g, b) => {
  // Ensure values are numbers and within valid range (0-255)
  const safeValue = (val) => {
    const num = Number(val);
    if (isNaN(num)) return 0;
    return Math.max(0, Math.min(255, Math.round(num)));
  };

  try {
    const values = [r, g, b].map(safeValue);
    return `#${values.map(x => x.toString(16).padStart(2, '0')).join('')}`;
  } catch (error) {
    console.error('Error in rgbToHex:', error);
    return '#000000'; // Fallback to black if conversion fails
  }
};

// Calculate relative luminance for contrast calculations
const getLuminance = (hex) => {
  const { r, g, b } = hexToRgb(hex);
  const [rs, gs, bs] = [r, g, b].map(c => {
    c = c / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
};

// Calculate contrast ratio between two colors
const getContrastRatio = (color1, color2) => {
  const l1 = getLuminance(color1);
  const l2 = getLuminance(color2);
  const lighter = Math.max(l1, l2);
  const darker = Math.min(l1, l2);
  return (lighter + 0.05) / (darker + 0.05);
};

// Adjust color to meet minimum contrast ratio
const ensureContrast = (color, backgroundColor, minContrast = 4.5) => {
  let { r, g, b } = hexToRgb(color);
  const currentContrast = getContrastRatio(color, backgroundColor);
  
  if (currentContrast >= minContrast) return color;

  const targetLuminance = getLuminance(backgroundColor);
  const step = targetLuminance > 0.5 ? -5 : 5;
  
  while (getContrastRatio(rgbToHex(r, g, b), backgroundColor) < minContrast) {
    r = Math.max(0, Math.min(255, r + step));
    g = Math.max(0, Math.min(255, g + step));
    b = Math.max(0, Math.min(255, b + step));
  }

  return rgbToHex(r, g, b);
};

// Improved darken function with luminance preservation
const darkenColor = (hex, factor = 0.8) => {
  const { r, g, b } = hexToRgb(hex);
  const hsl = rgbToHsl(r, g, b);
  hsl.l = Math.max(0, hsl.l * factor);
  const rgb = hslToRgb(hsl.h, hsl.s, hsl.l);
  return rgbToHex(rgb.r, rgb.g, rgb.b);
};

// HSL conversion utilities (for better color manipulation)
const rgbToHsl = (r, g, b) => {
  r /= 255;
  g /= 255;
  b /= 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h, s, l = (max + min) / 2;

  if (max === min) {
    h = s = 0;
  } else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      case b: h = (r - g) / d + 4; break;
    }
    h /= 6;
  }

  return { h, s, l };
};

const hslToRgb = (h, s, l) => {
  let r, g, b;

  if (s === 0) {
    r = g = b = l;
  } else {
    const hue2rgb = (p, q, t) => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1/6) return p + (q - p) * 6 * t;
      if (t < 1/2) return q;
      if (t < 2/3) return p + (q - p) * (2/3 - t) * 6;
      return p;
    };

    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = hue2rgb(p, q, h + 1/3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1/3);
  }

  return {
    r: Math.round(r * 255),
    g: Math.round(g * 255),
    b: Math.round(b * 255)
  };
};

// Modify the getStylePresets function to be more defensive
export const getStylePresets = (gameTypeColor, businessProfile, gameType) => {
  try {
    if (typeof gameTypeColor !== 'string') {
      gameTypeColor = DEFAULT_COLORS.primary;
    }

    if (typeof gameType !== 'string') {
      gameType = '';
    }

    const defaultTheme = {
      colors: {
        primary: gameTypeColor || DEFAULT_COLORS.primary,
        accent: DEFAULT_COLORS.accent
      },
      typography: {
        primaryFont: DEFAULT_FONTS.primary,
        secondaryFont: DEFAULT_FONTS.secondary,
        titleFont: DEFAULT_FONTS.primary,
        subtitleFont: DEFAULT_FONTS.secondary,
        scoreFont: DEFAULT_FONTS.primary,
        scoreValueFont: DEFAULT_FONTS.primary,
        leaderboardTitleFont: DEFAULT_FONTS.primary,
        rankFont: DEFAULT_FONTS.secondary,
        nameFont: DEFAULT_FONTS.primary,
        joinButtonFont: DEFAULT_FONTS.primary,
        ctaButtonFont: DEFAULT_FONTS.primary
      }
    };

    const theme = defaultTheme;
    if (businessProfile?.theme?.colors?.primary) {
      theme.colors.primary = businessProfile.theme.colors.primary;
    }
    if (businessProfile?.theme?.colors?.accent) {
      theme.colors.accent = businessProfile.theme.colors.accent;
    }
    if (businessProfile?.theme?.typography) {
      const { primaryFont, secondaryFont } = businessProfile.theme.typography;
      
      theme.typography = {
        primaryFont: primaryFont || DEFAULT_FONTS.primary,
        secondaryFont: secondaryFont || DEFAULT_FONTS.secondary,
        titleFont: primaryFont || DEFAULT_FONTS.primary,
        subtitleFont: secondaryFont || DEFAULT_FONTS.secondary,
        scoreFont: primaryFont || DEFAULT_FONTS.primary,
        scoreValueFont: primaryFont || DEFAULT_FONTS.primary,
        leaderboardTitleFont: primaryFont || DEFAULT_FONTS.primary,
        rankFont: secondaryFont || DEFAULT_FONTS.secondary,
        nameFont: primaryFont || DEFAULT_FONTS.primary,
        joinButtonFont: primaryFont || DEFAULT_FONTS.primary,
        ctaButtonFont: primaryFont || DEFAULT_FONTS.primary
      };
    }

    const primaryColor = theme.colors.primary;
    const accentColor = theme.colors.accent;

    let gradientEndColor;
    try {
      const rgb = hexToRgb(primaryColor);
      if (!rgb) throw new Error('Invalid RGB conversion');
      
      const hsl = rgbToHsl(rgb.r, rgb.g, rgb.b);
      if (!hsl) throw new Error('Invalid HSL conversion');
      
      const lighterPrimary = hslToRgb(hsl.h, hsl.s, Math.min(hsl.l * 1.2, 0.9));
      if (!lighterPrimary) throw new Error('Invalid lighter color conversion');
      
      gradientEndColor = rgbToHex(lighterPrimary.r, lighterPrimary.g, lighterPrimary.b);
      if (!gradientEndColor) throw new Error('Invalid hex conversion');
    } catch (colorError) {
      gradientEndColor = primaryColor;
    }

    // Return basic presets if anything goes wrong
    const presets = {
      default: {
        colors: {
          primary: primaryColor,
          accent: accentColor,
          background: {
            gradient: {
              start: '#f8fafc',
              end: '#e7e5e4'
            }
          },
          cardBackground: '#ffffff',
          cardOpacity: 0.97,
          titleText: '#1c1917',
          subtitleText: '#57534e',
          buttonBackground: '#0369a1',
          buttonOpacity: 1,
          buttonText: '#ffffff',
          buttonBorder: '#075985',
          searchButtonBackground: '#0369a1',
          searchButtonText: '#ffffff',
          searchButtonBorder: '#075985',
          headlineText: '#1c1917',
          subheadlineText: '#57534e',
          results: {
            cardBackground: '#ffffff',
            cardBackgroundOpacity: 0.97,
            scoreLabelText: '#78716c',
            scoreValueText: '#1c1917',
            joinButtonGradientFrom: '#0369a1',
            joinButtonGradientTo: '#075985',
            joinButtonText: '#ffffff'
          },
          quizResults: {
            questionText: '#1c1917',
            answerText: '#57534e',
            showAnswerText: '#57534e'
          },
          endCard: {
            background: '#ffffff',
            backgroundOpacity: 0.97,
            playAgainBackground: '#0369a1',
            playAgainText: '#ffffff',
            shareBackground: '#7c3aed',
            shareText: '#ffffff',
            ctaButtonBackground: '#059669',
            ctaButtonText: '#ffffff',
            ctaButtonOpacity: 1
          },
          leaderboard: {
            background: '#ffffff',
            backgroundOpacity: 0.97,
            leaderboardTitleText: '#1c1917',
            rankText: '#78716c',
            nameText: '#1c1917',
            scoreText: '#0369a1',
            userBackground: '#fafaf9',
            tabPrimary: '#0369a1',
            tabSecondary: '#78716c',
            joinButtonGradientFrom: '#0369a1',
            joinButtonGradientTo: '#075985',
            joinButtonText: '#ffffff'
          }
        },
        typography: {
          primaryFont: 'Roboto',
          fontWeight: '600'
        }
      }
    };

    // Only add other presets if we have valid colors
    if (primaryColor && accentColor) {

      // Transparent preset - Glass morphism style
      presets.transparent = {
        colors: {
          background: {
            gradient: {
              start: '#667eea',
              end: '#764ba2'
            }
          },
          cardBackground: '#ffffff',
          cardOpacity: 0.15,
          titleText: '#ffffff',
          subtitleText: '#f1f5f9',
          buttonBackground: 'rgba(255, 255, 255, 0.4)',
          buttonOpacity: 1,
          buttonText: '#ffffff',
          buttonBorder: 'rgba(255, 255, 255, 0.5)',
          searchButtonBackground: 'rgba(255, 255, 255, 0.4)',
          searchButtonText: '#ffffff',
          searchButtonBorder: 'rgba(255, 255, 255, 0.5)',
          headlineText: '#ffffff',
          subheadlineText: '#f1f5f9',
          results: {
            cardBackground: '#ffffff',
            cardBackgroundOpacity: 0.15,
            scoreLabelText: '#f1f5f9',
            scoreValueText: '#ffffff',
            joinButtonGradientFrom: 'rgba(255, 255, 255, 0.25)',
            joinButtonGradientTo: 'rgba(255, 255, 255, 0.15)',
            joinButtonText: '#ffffff'
          },
          quizResults: {
            questionText: '#ffffff',
            answerText: '#f1f5f9',
            showAnswerText: '#f1f5f9'
          },
          endCard: {
            background: '#ffffff',
            backgroundOpacity: 0.15,
            playAgainBackground: 'rgba(255, 255, 255, 0.25)',
            playAgainText: '#ffffff',
            shareBackground: 'rgba(255, 255, 255, 0.2)',
            shareText: '#ffffff',
            ctaButtonBackground: 'rgba(255, 255, 255, 0.3)',
            ctaButtonText: '#ffffff',
            ctaButtonOpacity: 1
          },
          leaderboard: {
            background: '#ffffff',
            backgroundOpacity: 0.15,
            leaderboardTitleText: '#ffffff',
            rankText: '#f1f5f9',
            nameText: '#ffffff',
            scoreText: '#f8fafc',
            userBackground: 'rgba(255, 255, 255, 0.1)',
            tabPrimary: 'rgba(255, 255, 255, 0.3)',
            tabSecondary: 'rgba(255, 255, 255, 0.15)',
            joinButtonGradientFrom: 'rgba(255, 255, 255, 0.25)',
            joinButtonGradientTo: 'rgba(255, 255, 255, 0.15)',
            joinButtonText: '#ffffff'
          }
        },
        typography: {
          primaryFont: 'Inter',
          fontWeight: 'bold'
        }
      };

      // Dark preset - Modern deep dark theme
      presets.dark = {
        colors: {
          background: {
            gradient: {
              start: '#0a0a0f',
              end: '#1a1a2e'
            }
          },
          cardBackground: '#0f0f23',
          cardOpacity: 0.98,
          titleText: '#f8fafc',
          subtitleText: '#94a3b8',
          buttonBackground: '#1f2937',
          buttonText: '#ffffff',
          buttonBorder: '#374151',
          searchButtonBackground: '#3b82f6',
          searchButtonText: '#ffffff',
          searchButtonBorder: '#1d4ed8',
          headlineText: '#f8fafc',
          subheadlineText: '#94a3b8',
          results: {
            cardBackground: '#0f0f23',
            cardBackgroundOpacity: 0.98,
            scoreLabelText: '#94a3b8',
            scoreValueText: '#f8fafc',
            joinButtonGradientFrom: '#3b82f6',
            joinButtonGradientTo: '#1d4ed8',
            joinButtonText: '#ffffff'
          },
          quizResults: {
            questionText: '#f8fafc',
            answerText: '#94a3b8',
            showAnswerText: '#94a3b8'
          },
          endCard: {
            background: '#0f0f23',
            backgroundOpacity: 0.98,
            playAgainBackground: '#3b82f6',
            playAgainText: '#ffffff',
            shareBackground: '#6366f1',
            shareText: '#ffffff',
            ctaButtonBackground: '#8b5cf6',
            ctaButtonText: '#ffffff',
            ctaButtonOpacity: 1
          },
          leaderboard: {
            background: '#0f0f23',
            backgroundOpacity: 0.98,
            leaderboardTitleText: '#f8fafc',
            rankText: '#94a3b8',
            nameText: '#f8fafc',
            scoreText: '#60a5fa',
            userBackground: '#1e293b',
            tabPrimary: '#3b82f6',
            tabSecondary: '#64748b',
            joinButtonGradientFrom: '#3b82f6',
            joinButtonGradientTo: '#1d4ed8',
            joinButtonText: '#ffffff'
          }
        },
        typography: {
          primaryFont: 'Inter',
          fontWeight: 'bold'
        }
      };

      // Retro preset
      presets.retro = {
        colors: {
          background: {
            gradient: {
              start: '#2C3E50',
              end: '#1A2530'
            }
          },
          cardBackground: '#34495E',
          cardOpacity: 0.95,
          titleText: '#F1C40F',
          subtitleText: '#ECF0F1',
          buttonBackground: '#E74C3C',
          buttonText: '#FFFFFF',
          searchButtonBackground: '#E74C3C',
          searchButtonText: '#FFFFFF',
          searchButtonBorder: '#C0392B',
          headlineText: '#F1C40F',
          subheadlineText: '#ECF0F1',
          results: {
            cardBackground: '#34495E',
            cardBackgroundOpacity: 0.95,
            scoreLabelText: '#ECF0F1',
            scoreValueText: '#F1C40F',
            joinButtonGradientFrom: '#E74C3C',
            joinButtonGradientTo: '#C0392B',
            joinButtonText: '#FFFFFF'
          },
          quizResults: {
            questionText: '#F1C40F',
            answerText: '#ECF0F1',
            showAnswerText: '#ECF0F1'
          },
          endCard: {
            background: '#34495E',
            backgroundOpacity: 0.95,
            playAgainBackground: '#E74C3C',
            playAgainText: '#FFFFFF',
            shareBackground: '#3498DB',
            shareText: '#FFFFFF',
            ctaButtonBackground: '#2ECC71',
            ctaButtonText: '#FFFFFF',
            ctaButtonOpacity: 1
          },
          leaderboard: {
            background: '#34495E',
            backgroundOpacity: 0.95,
            leaderboardTitleText: '#F1C40F',
            rankText: '#ECF0F1',
            nameText: '#FFFFFF',
            scoreText: '#2ECC71',
            userBackground: '#2C3E50',
            tabPrimary: '#E74C3C',
            tabSecondary: '#ECF0F1',
            joinButtonGradientFrom: '#E74C3C',
            joinButtonGradientTo: '#C0392B',
            joinButtonText: '#FFFFFF'
          },
          game: {
            topSection: '#2C3E50',
            topSectionOpacity: 1,
            textColor: '#F1C40F',
            marker: '#E74C3C',
            polyline: '#F1C40F',
          }
        },
        typography: {
          primaryFont: 'Share Tech Mono',
          fontWeight: 'bold'
        }
      };

      // Add game-specific styles based on gameType
      if (gameType) {
        try {
          Object.keys(presets).forEach(style => {
            if (gameType === 'snake') {
              presets[style].colors.game = {
                // Default style
                ...(style === 'default' && {
                  canvasColor: '#FFFFFF',
                  snakeColor: theme.colors?.primary || DEFAULT_COLORS.primary,
                  foodColor: theme.colors?.accent || DEFAULT_COLORS.accent,
                  controlButtonColor: '#FFFFFF',
                  controlButtonOpacity: 1,
                  controlIconColor: theme.colors?.primary || DEFAULT_COLORS.primary,
                  scoreText: '#1F2937'
                }),
                
                // Transparent style
                ...(style === 'transparent' && {
                  canvasColor: '#2a2d5a',
                  snakeColor: '#ffffff',
                  foodColor: '#00f5ff',
                  controlButtonColor: 'rgba(255, 255, 255, 0.2)',
                  controlButtonOpacity: 1,
                  controlIconColor: '#ffffff',
                  scoreText: '#ffffff'
                }),
                
                // Dark style
                ...(style === 'dark' && {
                  canvasColor: '#0f0f23',
                  snakeColor: '#3b82f6',
                  foodColor: '#FFFFFF',
                  controlButtonColor: '#1f2937',
                  controlButtonOpacity: 1,
                  controlIconColor: '#FFFFFF',
                  scoreText: '#FFFFFF'
                }),
                
                // Retro style - matching listmaster retro colors
                ...(style === 'retro' && {
                  canvasColor: '#34495E',
                  snakeColor: '#F1C40F',
                  foodColor: '#E74C3C',
                  controlButtonColor: '#34495E',
                  controlButtonOpacity: 1,
                  controlIconColor: '#ECF0F1',
                  scoreText: '#F1C40F'
                })
              };

              // Add typography settings for each style
              presets[style].typography = {
                primaryFont: style === 'retro' ? 'Share Tech Mono' : 'Inter',
                fontWeight: 'bold'
              };
            } else if (gameType === 'hint') {
              presets[style].colors.game = {
                // Default style
                ...(style === 'default' && {
                  hintBackground: '#FFFFFF',
                  hintBackgroundOpacity: 0.9,
                  hintText: '#1F2937',
                  inputBackground: '#FFFFFF',
                  inputBackgroundOpacity: 0.9,
                  inputText: '#1F2937',
                  inputBorder: '#E5E7EB',
                  inputPlaceholder: '#9CA3AF',
                  resultBackground: '#FFFFFF',
                  resultBackgroundOpacity: 0.9,
                  resultText: '#1F2937',
                  resultHoverBackground: '#F3F4F6',
                  keyboardBackground: '#F3F4F6',
                  keyboardOpacity: 0.9,
                  keyboardButtonBackground: '#FFFFFF',
                  keyboardButtonOpacity: 1,
                  keyboardText: '#1F2937',
                  keyboardBorder: '#E5E7EB',
                  skipButtonBackground: '#4B5563',
                  skipButtonBackgroundOpacity: 0.1,
                  skipButtonText: '#4B5563',
                  skipButtonHoverBackground: '#6B728020'
                }),



                // Transparent style
                ...(style === 'transparent' && {
                  hintBackground: 'rgba(255, 255, 255, 0.15)',
                  hintBackgroundOpacity: 1,
                  hintText: '#ffffff',
                  inputBackground: 'rgba(255, 255, 255, 0.15)',
                  inputBackgroundOpacity: 1,
                  inputText: '#ffffff',
                  inputBorder: 'rgba(255, 255, 255, 0.3)',
                  inputPlaceholder: '#f1f5f9',
                  resultBackground: 'rgba(255, 255, 255, 0.15)',
                  resultBackgroundOpacity: 1,
                  resultText: '#ffffff',
                  resultHoverBackground: 'rgba(255, 255, 255, 0.25)',
                  keyboardBackground: 'rgba(255, 255, 255, 0.1)',
                  keyboardOpacity: 1,
                  keyboardButtonBackground: 'rgba(255, 255, 255, 0.2)',
                  keyboardButtonOpacity: 1,
                  keyboardText: '#ffffff',
                  keyboardBorder: 'rgba(255, 255, 255, 0.3)',
                  skipButtonBackground: 'rgba(255, 255, 255, 0.2)',
                  skipButtonBackgroundOpacity: 1,
                  skipButtonText: '#ffffff',
                  skipButtonHoverBackground: 'rgba(255, 255, 255, 0.3)'
                }),

                // Dark style
                ...(style === 'dark' && {
                  hintBackground: '#1F2937',
                  hintBackgroundOpacity: 0.95,
                  hintText: '#FFFFFF',
                  inputBackground: '#1F2937',
                  inputBackgroundOpacity: 0.95,
                  inputText: '#FFFFFF',
                  inputBorder: '#374151',
                  inputPlaceholder: '#E5E7EB',
                  resultBackground: '#1F2937',
                  resultBackgroundOpacity: 0.95,
                  resultText: '#FFFFFF',
                  resultHoverBackground: '#374151',
                  keyboardBackground: '#111827',
                  keyboardOpacity: 0.95,
                  keyboardButtonBackground: '#1F2937',
                  keyboardButtonOpacity: 1,
                  keyboardText: '#FFFFFF',
                  keyboardBorder: 'transparent',
                  skipButtonBackground: '#FFFFFF',
                  skipButtonBackgroundOpacity: 0.1,
                  skipButtonText: '#FFFFFF',
                  skipButtonHoverBackground: 'rgba(255, 255, 255, 0.2)'
                }),

                // Retro style
                ...(style === 'retro' && {
                  // Header
                  headerBackground: '#2C3E50',
                  headerBackgroundOpacity: 1,
                  headerText: '#E74C3C',
                  headerBorder: '#34495E',
                  guessesText: '#ECF0F1',
                  guessesOpacity: 1,

                  // Surface/Answer slots
                  surfaceBackground: '#34495E',
                  surfaceBackgroundOpacity: 1,
                  filledSlotBackground: '#FFEB3B',
                  filledSlotBackgroundOpacity: 1,
                  text: '#ECF0F1',
                  border: '#2C3E50',
                  rankNumberColor: '#E74C3C',
                  rankNumberOpacity: 1,
                  contextText: '#ECF0F1',
                  contextOpacity: 1,

                  // Input
                  inputBackground: '#34495E',
                  inputBackgroundOpacity: 1,
                  inputText: '#ECF0F1',
                  inputBorder: '#BFC9CA',
                  inputPlaceholder: '#ECF0F1',

                  // Keyboard
                  keyboardBackground: '#1A2530',
                  keyboardOpacity: 1,
                  KeyBackground: '#34495E',
                  keyboardButtonBackground: '#34495E',
                  keyboardButtonOpacity: 1,
                  keyboardText: '#ECF0F1',
                  keyboardBorder: '#BFC9CA',

                  // Animations
                  checkingColor: '#E74C3C',
                  successColor: '#FFEB3B',
                  checkingAnimationBackground: '#E74C3C',

                  // Hint specific
                  hintBackground: '#2C3E50',
                  hintBackgroundOpacity: 1,
                  hintText: '#F1C40F',
                  hintBorder: '#F1C40F',
                  hintIconColor: '#F1C40F',
                  resultBackground: '#34495E',
                  resultBackgroundOpacity: 1,
                  resultText: '#ECF0F1',
                  resultHoverBackground: '#2C3E50',
                  skipButtonBackground: '#E74C3C',
                  skipButtonBackgroundOpacity: 0.2,
                  skipButtonText: '#E74C3C',
                  skipButtonHoverBackground: '#E74C3C40',
                  giveUpText: '#E74C3C',
                  giveUpBackground: '#E74C3C20',
                  giveUpHoverBackground: '#E74C3C40',
                  searchButtonBackground: '#E74C3C',
                  searchButtonText: '#FFFFFF',
                  searchButtonBorder: '#BFC9CA'
                })
              };

              // Add typography settings for each style
              presets[style].typography = {
                primaryFont: style === 'retro' ? 'Share Tech Mono' : 'Inter',
                fontWeight: 'bold'
              };
            } else if (gameType === 'mapselect') {
              presets[style].colors.game = {
                // Default style
                ...(style === 'default' && {
                  inputBackground: '#FFFFFF',
                  inputBackgroundOpacity: 0.9,
                  searchButtonBackground: '#4F46E5',
                  searchButtonOpacity: 1,
                  searchButtonBorder: '#4F46E5',
                  searchButtonText: '#FFFFFF',
                  inputText: '#111827',
                  inputBorder: '#D1D5DB',
                  inputPlaceholder: '#6B7280',
                  resultsBackground: '#FFFFFF',
                  resultsBackgroundOpacity: 0.9,
                  resultsText: '#111827',
                  resultsHoverBackground: '#F3F4F6',
                  keyboard: {
                    background: '#FFFFFF',
                    opacity: 0.9,
                    buttonBackground: '#E5E7EB',
                    buttonOpacity: 1,
                    text: '#111827',
                    border: '#D1D5DB',
                    uppercase: true
                  }
                }),



                // Transparent style
                ...(style === 'transparent' && {
                  inputBackground: 'rgba(255, 255, 255, 0.15)',
                  inputBackgroundOpacity: 1,
                  searchButtonBackground: 'rgba(255, 255, 255, 0.25)',
                  searchButtonOpacity: 1,
                  searchButtonBorder: 'rgba(255, 255, 255, 0.3)',
                  searchButtonText: '#ffffff',
                  inputText: '#ffffff',
                  inputBorder: 'rgba(255, 255, 255, 0.3)',
                  inputPlaceholder: '#f1f5f9',
                  resultsBackground: 'rgba(255, 255, 255, 0.15)',
                  resultsBackgroundOpacity: 1,
                  resultsText: '#ffffff',
                  resultsHoverBackground: 'rgba(255, 255, 255, 0.25)',
                  keyboardBackground: 'rgba(255, 255, 255, 0.1)',
                  keyboardOpacity: 1,
                  keyboardButtonBackground: 'rgba(255, 255, 255, 0.2)',
                  keyboardButtonOpacity: 1,
                  keyboardText: '#FFFFFF',
                  keyboardBorder: 'transparent',
                  skipButtonBackground: '#FFFFFF',
                  skipButtonBackgroundOpacity: 0.1,
                  skipButtonText: '#FFFFFF',
                  skipButtonHoverBackground: 'rgba(255, 255, 255, 0.2)'
                }),

                // Dark style
                ...(style === 'dark' && {
                  inputBackground: '#1F2937',
                  inputBackgroundOpacity: 0.9,
                  searchButtonBackground: '#374151',
                  searchButtonOpacity: 1,
                  searchButtonBorder: '#374151',
                  searchButtonText: '#FFFFFF',
                  inputText: '#FFFFFF',
                  inputBorder: '#E5E7EB',
                  inputPlaceholder: '#9CA3AF',
                  resultsBackground: '#1F2937',
                  resultsBackgroundOpacity: 0.9,
                  resultsText: '#FFFFFF',
                  resultsHoverBackground: '#374151',
                  keyboardBackground: '#111827',
                  keyboardOpacity: 0.9,
                  keyboardButtonBackground: '#1F2937',
                  keyboardButtonOpacity: 1,
                  keyboardText: '#FFFFFF',
                  keyboardBorder: 'transparent',
                  skipButtonBackground: '#FFFFFF',
                  skipButtonBackgroundOpacity: 0.1,
                  skipButtonText: '#FFFFFF',
                  skipButtonHoverBackground: 'rgba(255, 255, 255, 0.2)'
                }),

                // Retro style
                ...(style === 'retro' && {
                  inputBackground: '#34495E',
                  inputBackgroundOpacity: 1,
                  searchButtonBackground: '#E74C3C',
                  searchButtonOpacity: 1,
                  searchButtonBorder: '#BFC9CA',
                  searchButtonText: '#FFFFFF',
                  inputText: '#ECF0F1',
                  inputBorder: '#BFC9CA',
                  inputPlaceholder: '#ECF0F1',
                  resultsBackground: '#34495E',
                  resultsBackgroundOpacity: 1,
                  resultsText: '#ECF0F1',
                  resultsHoverBackground: '#2C3E50',
                  keyboardBackground: '#1A2530',
                  keyboardOpacity: 1,
                  keyboardButtonBackground: '#34495E',
                  keyboardButtonOpacity: 1,
                  keyboardText: '#ECF0F1',
                  keyboardBorder: '#BFC9CA'
                })
              };

              // Add typography settings for each style
              presets[style].typography = {
                primaryFont: style === 'retro' ? 'Share Tech Mono' : 'Inter',
                fontWeight: 'bold'
              };
            } else if (gameType === 'quiz') {
              presets[style].colors.game = {
                // Default style
                ...(style === 'default' && {
                  surface: {
                    background: '#FFFFFF',
                    opacity: 0.9,
                    questionText: '#1F2937',
                    questionBadgeBackground: '#4B5563',
                    questionBadgeOpacity: 0.1,
                    questionBadgeText: '#4B5563',
                    answerBackground: '#FFFFFF',
                    answerOpacity: 0.9,
                    answerText: '#1F2937',
                    hintText: '#4B5563'
                  }
                }),



                // Transparent style
                ...(style === 'transparent' && {
                  surface: {
                    background: 'rgba(255, 255, 255, 0.15)',
                    opacity: 1,
                    questionText: '#ffffff',
                    questionBadgeBackground: 'rgba(255, 255, 255, 0.2)',
                    questionBadgeOpacity: 1,
                    questionBadgeText: '#ffffff',
                    answerBackground: 'rgba(255, 255, 255, 0.1)',
                    answerOpacity: 1,
                    answerText: '#ffffff',
                    hintText: '#f8fafc'
                  }
                }),

                // Dark style
                ...(style === 'dark' && {
                  surface: {
                    background: '#111827',
                    opacity: 0.95,
                    questionText: '#FFFFFF',
                    questionBadgeBackground: '#374151',
                    questionBadgeOpacity: 0.1,
                    questionBadgeText: '#9CA3AF',
                    answerBackground: '#1F2937',
                    answerOpacity: 0.95,
                    answerText: '#FFFFFF',
                    hintText: '#9CA3AF'
                  }
                }),

                // Retro style
                ...(style === 'retro' && {
                  surface: {
                    background: '#34495E',
                    opacity: 1,
                    questionText: '#ECF0F1',
                    questionBadgeBackground: '#E74C3C',
                    questionBadgeOpacity: 0.2,
                    questionBadgeText: '#E74C3C',
                    answerBackground: '#2C3E50',
                    answerOpacity: 1,
                    answerText: '#ECF0F1',
                    hintText: '#F1C40F',
                    hintBackground: '#2C3E50',
                    hintBackgroundOpacity: 1,
                    hintBorder: '#F1C40F',
                    hintIconColor: '#F1C40F'
                  }
                })
              };

              // Add typography settings for each style
              presets[style].typography = {
                primaryFont: style === 'retro' ? 'Share Tech Mono' : 'Inter',
                fontWeight: 'bold'
              };
            } else if (gameType === 'pindrop') {
              presets[style].colors.game = {
                // Default style
                ...(style === 'default' && {
                  topSection: '#145203',
                  textColor: '#FFFFFF',
                  topSectionOpacity: 1
                }),



                // Transparent style
                ...(style === 'transparent' && {
                  topSection: 'rgba(255, 255, 255, 0.15)',
                  textColor: '#ffffff',
                  topSectionOpacity: 1,
                  polyline: '#f8fafc',
                  marker: '#ffffff'
                }),

                // Dark style
                ...(style === 'dark' && {
                  topSection: '#111827',
                  textColor: '#FFFFFF',
                  topSectionOpacity: 0.95
                })
              };

              // Add typography settings for each style
              presets[style].typography = {
                primaryFont: style === 'retro' ? 'Share Tech Mono' : 'Inter',
                fontWeight: 'bold'
              };
            } else if (gameType === 'listmaster') {
              presets[style].colors.game = {
                // Default style
                ...(style === 'default' && {
                  // Header
                  headerBackground: '#4F46E5',
                  headerBackgroundOpacity: 1,
                  headerText: '#FFFFFF',
                  headerBorder: '#E5E7EB',
                  guessesText: '#FFFFFF',
                  guessesOpacity: 0.6,

                  // Surface/Answer slots
                  surfaceBackground: '#FFFFFF',
                  surfaceBackgroundOpacity: 0.9,
                  filledSlotBackground: '#4F46E520',
                  filledSlotBackgroundOpacity: 0.4,
                  text: '#1F2937',
                  border: '#E5E7EB',
                  rankNumberColor: '#6B7280',
                  rankNumberOpacity: 0.6,
                  contextText: '#6B7280',
                  contextOpacity: 0.75,

                  // Input
                  inputBackground: '#FFFFFF',
                  inputBackgroundOpacity: 0.9,
                  inputText: '#1F2937',
                  inputBorder: '#E5E7EB',
                  inputPlaceholder: '#6B7280',

                  // Keyboard
                  keyboardBackground: '#FFFFFF',
                  keyboardOpacity: 0.9,
                  KeyBackground: '#E5E7EB',
                  keyboardButtonOpacity: 1,
                  keyboardText: '#1F2937',
                  keyboardBorder: '#E5E7EB',

                  // Animations
                  checkingColor: '#EF4444',
                  successColor: '#22C55E'
                }),



                // Transparent style
                ...(style === 'transparent' && {
                  // Header
                  headerBackground: 'rgba(255, 255, 255, 0.15)',
                  headerBackgroundOpacity: 1,
                  headerText: '#ffffff',
                  headerBorder: 'rgba(255, 255, 255, 0.3)',
                  guessesText: '#f1f5f9',
                  guessesOpacity: 1,

                  // Surface/Answer slots
                  surfaceBackground: 'rgba(255, 255, 255, 0.1)',
                  surfaceBackgroundOpacity: 1,
                  filledSlotBackground: 'rgba(255, 255, 255, 0.25)',
                  filledSlotBackgroundOpacity: 1,
                  text: '#ffffff',
                  border: 'rgba(255, 255, 255, 0.3)',
                  rankNumberColor: '#f8fafc',
                  rankNumberOpacity: 1,
                  contextText: '#f1f5f9',
                  contextOpacity: 1,

                  // Input
                  inputBackground: 'rgba(255, 255, 255, 0.15)',
                  inputBackgroundOpacity: 1,
                  inputText: '#ffffff',
                  inputBorder: 'rgba(255, 255, 255, 0.3)',
                  inputPlaceholder: '#f1f5f9',

                  // Keyboard
                  keyboardBackground: 'rgba(255, 255, 255, 0.1)',
                  keyboardOpacity: 1,
                  'Key Background': 'rgba(255, 255, 255, 0.2)',
                  keyboardButtonOpacity: 1,
                  keyboardText: '#ffffff',
                  keyboardBorder: 'rgba(255, 255, 255, 0.3)',

                  // Animations
                  checkingColor: '#ff4444',
                  successColor: '#22ff88'
                }),

                // Dark style
                ...(style === 'dark' && {
                  // Header
                  headerBackground: '#374151',
                  headerBackgroundOpacity: 0.95,
                  headerText: '#FFFFFF',
                  headerBorder: '#374151',
                  guessesText: '#FFFFFF',
                  guessesOpacity: 0.6,

                  // Surface/Answer slots
                  surfaceBackground: '#111827',
                  surfaceBackgroundOpacity: 0.95,
                  filledSlotBackground: 'rgba(255, 255, 255, 0.2)',
                  filledSlotBackgroundOpacity: 0.4,
                  text: '#FFFFFF',
                  border: '#374151',
                  rankNumberColor: '#9CA3AF',
                  rankNumberOpacity: 0.6,
                  contextText: '#9CA3AF',
                  contextOpacity: 0.75,

                  // Input
                  inputBackground: '#1F2937',
                  inputBackgroundOpacity: 0.95,
                  inputText: '#FFFFFF',
                  inputBorder: '#374151',
                  inputPlaceholder: '#E5E7EB',

                  // Keyboard
                  keyboardBackground: '#111827',
                  keyboardOpacity: 0.95,
                  'Key Background': '#1F2937',
                  keyboardButtonOpacity: 1,
                  keyboardText: '#FFFFFF',
                  keyboardBorder: 'transparent',

                  // Animations
                  checkingColor: '#EF4444',
                  successColor: '#22C55E'
                }),

                // Retro style
                ...(style === 'retro' && {
                  // Header
                  headerBackground: '#2C3E50',
                  headerBackgroundOpacity: 1,
                  headerText: '#F1C40F',
                  headerBorder: '#34495E',
                  guessesText: '#ECF0F1',
                  guessesOpacity: 1,

                  // Surface/Answer slots
                  surfaceBackground: '#34495E',
                  surfaceBackgroundOpacity: 1,
                  filledSlotBackground: '#F1C40F',
                  filledSlotBackgroundOpacity: 1,
                  text: '#ECF0F1',
                  border: '#2C3E50',
                  rankNumberColor: '#E74C3C',
                  rankNumberOpacity: 1,
                  contextText: '#ECF0F1',
                  contextOpacity: 1,

                  // Input
                  inputBackground: '#34495E',
                  inputBackgroundOpacity: 1,
                  inputText: '#ECF0F1',
                  inputBorder: '#BFC9CA',
                  inputPlaceholder: '#ECF0F1',

                  // Keyboard
                  keyboardBackground: '#1A2530',
                  keyboardOpacity: 1,
                  'Key Background': '#34495E',
                  keyboardButtonBackground: '#34495E',
                  keyboardButtonOpacity: 1,
                  keyboardText: '#ECF0F1',
                  keyboardBorder: '#BFC9CA',

                  // Animations
                  checkingColor: '#E74C3C',
                  successColor: '#FFEB3B',
                  checkingAnimationBackground: '#E74C3C',

                  // Hint specific
                  hintBackground: '#2C3E50',
                  hintBackgroundOpacity: 1,
                  hintText: '#F1C40F',
                  hintBorder: '#F1C40F',
                  hintIconColor: '#F1C40F',
                  resultBackground: '#34495E',
                  resultBackgroundOpacity: 1,
                  resultText: '#ECF0F1',
                  resultHoverBackground: '#2C3E50',
                  skipButtonBackground: '#E74C3C',
                  skipButtonBackgroundOpacity: 0.2,
                  skipButtonText: '#E74C3C',
                  skipButtonHoverBackground: '#E74C3C40',
                  giveUpText: '#E74C3C',
                  giveUpBackground: '#E74C3C20',
                  giveUpHoverBackground: '#E74C3C40',
                  searchButtonBackground: '#E74C3C',
                  searchButtonText: '#FFFFFF',
                  searchButtonBorder: '#BFC9CA'
                })
              };

              // Add typography settings for each style
              presets[style].typography = {
                primaryFont: style === 'retro' ? 'Share Tech Mono' : 'Inter',
                fontWeight: 'bold'
              };
            } else if (gameType === 'cubefield') {
              presets[style].colors.game = {
                // Default style
                ...(style === 'default' && {
                  backgroundColor: '#FFFFFF',
                  playerColor: '#EF4444',
                  cubeColor: '#4F46E5',
                  gridColor: '#E5E7EB',
                  scoreBackground: '#FFFFFF',
                  scoreBackgroundOpacity: 0.9,
                  scoreText: '#1F2937',
                  controlButtonColor: '#1F2937',
                  controlButtonOpacity: 0.9,
                  controlIconColor: '#FFFFFF'
                }),



                // Transparent style - glass morphism
                ...(style === 'transparent' && {
                  backgroundColor: '#2a2d5a',
                  playerColor: '#ffffff',
                  cubeColor: '#00f5ff',
                  gridColor: '#4a4d7a',
                  scoreBackground: 'rgba(255, 255, 255, 0.15)',
                  scoreBackgroundOpacity: 1,
                  scoreText: '#ffffff',
                  controlButtonColor: 'rgba(255, 255, 255, 0.2)',
                  controlButtonOpacity: 1,
                  controlIconColor: '#ffffff'
                }),

                // Dark style - keep dark background
                ...(style === 'dark' && {
                  backgroundColor: '#111827',
                  playerColor: '#EF4444',
                  cubeColor: '#4F46E5',
                  gridColor: '#333333',
                  scoreBackground: '#1F2937',
                  scoreBackgroundOpacity: 0.9,
                  scoreText: '#FFFFFF',
                  controlButtonColor: '#1F2937',
                  controlButtonOpacity: 0.9,
                  controlIconColor: '#FFFFFF'
                }),

                // Retro style
                ...(style === 'retro' && {
                  backgroundColor: '#2C3E50',
                  playerColor: '#E74C3C',
                  cubeColor: '#F1C40F',
                  gridColor: '#34495E',
                  scoreBackground: '#34495E',
                  scoreBackgroundOpacity: 0.95,
                  scoreText: '#F1C40F',
                  controlButtonColor: '#34495E',
                  controlButtonOpacity: 0.8,
                  controlIconColor: '#F1C40F'
                })
              };

              // Add typography settings for each style
              presets[style].typography = {
                primaryFont: style === 'retro' ? 'Share Tech Mono' : (style === 'default' ? 'Roboto' : 'Inter'),
                fontWeight: 'bold'
              };
            }
          });
        } catch (gameError) {
          console.error('Error adding game-specific styles:', gameError);
        }
      }

      // Add Neon preset for various game types
      if (gameType === 'cubefield') {
        presets.neon = {
          colors: {
            background: {
              gradient: {
                start: '#0F0C29',
                end: '#24243e'
              }
            },
            cardBackground: '#1a1a2e',
            cardOpacity: 0.9,
            titleText: '#00f5ff',
            subtitleText: '#ff0080',
            buttonBackground: '#ff0080',
            buttonText: '#ffffff',
            headlineText: '#00f5ff',
            subheadlineText: '#ff0080',
            results: {
              cardBackground: '#1a1a2e',
              cardBackgroundOpacity: 0.9,
              scoreLabelText: '#00f5ff',
              scoreValueText: '#ffffff',
              joinButtonGradientFrom: '#ff0080',
              joinButtonGradientTo: '#ff1090',
              joinButtonText: '#ffffff'
            },
            endCard: {
              background: '#1a1a2e',
              backgroundOpacity: 0.9,
              playAgainBackground: '#ff0080',
              playAgainText: '#ffffff',
              shareBackground: '#00f5ff',
              shareText: '#000000',
              ctaButtonBackground: '#ff0080',
              ctaButtonText: '#ffffff',
              ctaButtonOpacity: 1
            },
            leaderboard: {
              background: '#1a1a2e',
              backgroundOpacity: 0.9,
              leaderboardTitleText: '#00f5ff',
              rankText: '#ff0080',
              nameText: '#ffffff',
              scoreText: '#00f5ff',
              userBackground: '#2a2a3e',
              tabPrimary: '#ff0080',
              tabSecondary: '#666699',
              joinButtonGradientFrom: '#ff0080',
              joinButtonGradientTo: '#ff1090',
              joinButtonText: '#ffffff'
            },
            game: {
              backgroundColor: '#0a0a1a',
              playerColor: '#00f5ff',
              cubeColor: '#ff0080',
              gridColor: '#1a1a3a',
              scoreBackground: '#1a1a2e',
              scoreBackgroundOpacity: 0.9,
              scoreText: '#00f5ff',
              controlButtonColor: '#1a1a2e',
              controlButtonOpacity: 0.9,
              controlIconColor: '#00f5ff'
            }
          },
          typography: {
            primaryFont: 'Orbitron',
            fontWeight: 'bold'
          }
        };

        // Add Monochrome preset specifically for cubefield game
        presets.monochrome = {
          colors: {
            background: {
              gradient: {
                start: '#ffffff',
                end: '#f8f9fa'
              }
            },
            cardBackground: '#ffffff',
            cardOpacity: 1,
            titleText: '#000000',
            subtitleText: '#6c757d',
            buttonBackground: '#000000',
            buttonText: '#ffffff',
            headlineText: '#000000',
            subheadlineText: '#6c757d',
            results: {
              cardBackground: '#ffffff',
              cardBackgroundOpacity: 1,
              scoreLabelText: '#6c757d',
              scoreValueText: '#000000',
              joinButtonGradientFrom: '#000000',
              joinButtonGradientTo: '#343a40',
              joinButtonText: '#ffffff'
            },
            endCard: {
              background: '#ffffff',
              backgroundOpacity: 1,
              playAgainBackground: '#000000',
              playAgainText: '#ffffff',
              shareBackground: '#6c757d',
              shareText: '#ffffff',
              ctaButtonBackground: '#343a40',
              ctaButtonText: '#ffffff',
              ctaButtonOpacity: 1
            },
            leaderboard: {
              background: '#ffffff',
              backgroundOpacity: 1,
              leaderboardTitleText: '#000000',
              rankText: '#6c757d',
              nameText: '#000000',
              scoreText: '#343a40',
              userBackground: '#f8f9fa',
              tabPrimary: '#000000',
              tabSecondary: '#adb5bd',
              joinButtonGradientFrom: '#000000',
              joinButtonGradientTo: '#343a40',
              joinButtonText: '#ffffff'
            },
            game: {
              backgroundColor: '#ffffff',
              playerColor: '#000000',
              cubeColor: '#6c757d',
              gridColor: '#e9ecef',
              scoreBackground: '#ffffff',
              scoreBackgroundOpacity: 1,
              scoreText: '#000000',
              controlButtonColor: '#ffffff',
              controlButtonOpacity: 1,
              controlIconColor: '#000000'
            }
          },
          typography: {
            primaryFont: 'Inter',
            fontWeight: 'normal'
          }
        };

        // Add Pastel Dreams preset specifically for cubefield game
        presets.pastel = {
          colors: {
            background: {
              gradient: {
                start: '#fef7ff',
                end: '#e0f2fe'
              }
            },
            cardBackground: '#ffffff',
            cardOpacity: 0.85,
            titleText: '#7c3aed',
            subtitleText: '#0ea5e9',
            buttonBackground: '#ec4899',
            buttonText: '#ffffff',
            headlineText: '#7c3aed',
            subheadlineText: '#0ea5e9',
            results: {
              cardBackground: '#ffffff',
              cardBackgroundOpacity: 0.85,
              scoreLabelText: '#0ea5e9',
              scoreValueText: '#1e293b',
              joinButtonGradientFrom: '#ec4899',
              joinButtonGradientTo: '#f97316',
              joinButtonText: '#ffffff'
            },
            endCard: {
              background: '#ffffff',
              backgroundOpacity: 0.85,
              playAgainBackground: '#ec4899',
              playAgainText: '#ffffff',
              shareBackground: '#06b6d4',
              shareText: '#ffffff',
              ctaButtonBackground: '#f59e0b',
              ctaButtonText: '#ffffff',
              ctaButtonOpacity: 1
            },
            leaderboard: {
              background: '#ffffff',
              backgroundOpacity: 0.85,
              leaderboardTitleText: '#7c3aed',
              rankText: '#06b6d4',
              nameText: '#1e293b',
              scoreText: '#dc2626',
              userBackground: '#f1f5f9',
              tabPrimary: '#ec4899',
              tabSecondary: '#94a3b8',
              joinButtonGradientFrom: '#ec4899',
              joinButtonGradientTo: '#f97316',
              joinButtonText: '#ffffff'
            },
            game: {
              backgroundColor: '#f8fafc',
              playerColor: '#06b6d4',
              cubeColor: '#ec4899',
              gridColor: '#e2e8f0',
              scoreBackground: '#ffffff',
              scoreBackgroundOpacity: 0.8,
              scoreText: '#7c3aed',
              controlButtonColor: '#ffffff',
              controlButtonOpacity: 0.8,
              controlIconColor: '#ec4899'
            }
          },
          typography: {
            primaryFont: 'Quicksand',
            fontWeight: 'normal'
          }
        };

        // Add Arcade preset specifically for cubefield game
        presets.arcade = {
          colors: {
            background: {
              gradient: {
                start: '#1E40AF',
                end: '#7C2D12'
              }
            },
            cardBackground: '#000000',
            cardOpacity: 0.9,
            titleText: '#FFFF00',
            subtitleText: '#00FF00',
            buttonBackground: '#FF0000',
            buttonText: '#FFFFFF',
            headlineText: '#FFFF00',
            subheadlineText: '#00FF00',
            results: {
              cardBackground: '#000000',
              cardBackgroundOpacity: 0.9,
              scoreLabelText: '#FFFF00',
              scoreValueText: '#FFFFFF',
              joinButtonGradientFrom: '#FF0000',
              joinButtonGradientTo: '#FF4000',
              joinButtonText: '#FFFFFF'
            },
            endCard: {
              background: '#000000',
              backgroundOpacity: 0.9,
              playAgainBackground: '#FF0000',
              playAgainText: '#FFFFFF',
              shareBackground: '#00FF00',
              shareText: '#000000',
              ctaButtonBackground: '#FFFF00',
              ctaButtonText: '#000000',
              ctaButtonOpacity: 1
            },
            leaderboard: {
              background: '#000000',
              backgroundOpacity: 0.9,
              leaderboardTitleText: '#FFFF00',
              rankText: '#00FF00',
              nameText: '#FFFFFF',
              scoreText: '#FF0000',
              userBackground: '#1E40AF',
              tabPrimary: '#FF0000',
              tabSecondary: '#00FF00',
              joinButtonGradientFrom: '#FF0000',
              joinButtonGradientTo: '#FF4000',
              joinButtonText: '#FFFFFF'
            },
            game: {
              backgroundColor: '#000000',
              playerColor: '#FFFF00',
              cubeColor: '#FF0000',
              gridColor: '#0000FF',
              scoreBackground: '#000000',
              scoreBackgroundOpacity: 0.8,
              scoreText: '#FFFF00',
              controlButtonColor: '#000000',
              controlButtonOpacity: 0.8,
              controlIconColor: '#FFFF00'
            }
          },
          typography: {
            primaryFont: 'Russo One',
            fontWeight: 'normal'
          }
        };
      } else if (gameType === 'listmaster') {
        // Add Monochrome preset for listmaster game
        presets.monochrome = {
          colors: {
            background: {
              gradient: {
                start: '#ffffff',
                end: '#f8f9fa'
              }
            },
            cardBackground: '#ffffff',
            cardOpacity: 1,
            titleText: '#000000',
            subtitleText: '#6c757d',
            buttonBackground: '#000000',
            buttonText: '#ffffff',
            headlineText: '#000000',
            subheadlineText: '#6c757d',
            results: {
              cardBackground: '#ffffff',
              cardBackgroundOpacity: 1,
              scoreLabelText: '#6c757d',
              scoreValueText: '#000000',
              joinButtonGradientFrom: '#000000',
              joinButtonGradientTo: '#343a40',
              joinButtonText: '#ffffff'
            },
            endCard: {
              background: '#ffffff',
              backgroundOpacity: 1,
              playAgainBackground: '#000000',
              playAgainText: '#ffffff',
              shareBackground: '#6c757d',
              shareText: '#ffffff',
              ctaButtonBackground: '#343a40',
              ctaButtonText: '#ffffff',
              ctaButtonOpacity: 1
            },
            leaderboard: {
              background: '#ffffff',
              backgroundOpacity: 1,
              leaderboardTitleText: '#000000',
              rankText: '#6c757d',
              nameText: '#000000',
              scoreText: '#343a40',
              userBackground: '#f8f9fa',
              tabPrimary: '#000000',
              tabSecondary: '#adb5bd',
              joinButtonGradientFrom: '#000000',
              joinButtonGradientTo: '#343a40',
              joinButtonText: '#ffffff'
            },
            game: {
              // Header
              headerBackground: '#ffffff',
              headerBackgroundOpacity: 1,
              headerText: '#000000',
              headerBorder: '#e9ecef',
              guessesText: '#6c757d',
              guessesOpacity: 1,

              // Surface/Answer slots
              surfaceBackground: '#ffffff',
              surfaceBackgroundOpacity: 1,
              filledSlotBackground: '#000000',
              filledSlotBackgroundOpacity: 1,
              text: '#000000',
              border: '#e9ecef',
              rankNumberColor: '#6c757d',
              rankNumberOpacity: 1,
              contextText: '#6c757d',
              contextOpacity: 1,

              // Input
              inputBackground: '#ffffff',
              inputBackgroundOpacity: 1,
              inputText: '#000000',
              inputBorder: '#e9ecef',
              inputPlaceholder: '#adb5bd',

              // Keyboard
              keyboardBackground: '#f8f9fa',
              keyboardOpacity: 1,
              keyboardButtonBackground: '#ffffff',
              keyboardButtonOpacity: 1,
              keyboardText: '#000000',
              keyboardBorder: '#e9ecef',

              // Animations
              checkingColor: '#6c757d',
              successColor: '#000000'
            }
          },
          typography: {
            primaryFont: 'Inter',
            fontWeight: 'normal'
          }
        };

        // Add Pastel Dreams preset for listmaster game
        presets.pastel = {
          colors: {
            background: {
              gradient: {
                start: '#fef7ff',
                end: '#e0f2fe'
              }
            },
            cardBackground: '#ffffff',
            cardOpacity: 0.85,
            titleText: '#7c3aed',
            subtitleText: '#0ea5e9',
            buttonBackground: '#ec4899',
            buttonText: '#ffffff',
            headlineText: '#7c3aed',
            subheadlineText: '#0ea5e9',
            results: {
              cardBackground: '#ffffff',
              cardBackgroundOpacity: 0.85,
              scoreLabelText: '#0ea5e9',
              scoreValueText: '#1e293b',
              joinButtonGradientFrom: '#ec4899',
              joinButtonGradientTo: '#f97316',
              joinButtonText: '#ffffff'
            },
            endCard: {
              background: '#ffffff',
              backgroundOpacity: 0.85,
              playAgainBackground: '#ec4899',
              playAgainText: '#ffffff',
              shareBackground: '#06b6d4',
              shareText: '#ffffff',
              ctaButtonBackground: '#f59e0b',
              ctaButtonText: '#ffffff',
              ctaButtonOpacity: 1
            },
            leaderboard: {
              background: '#ffffff',
              backgroundOpacity: 0.85,
              leaderboardTitleText: '#7c3aed',
              rankText: '#06b6d4',
              nameText: '#1e293b',
              scoreText: '#dc2626',
              userBackground: '#f1f5f9',
              tabPrimary: '#ec4899',
              tabSecondary: '#94a3b8',
              joinButtonGradientFrom: '#ec4899',
              joinButtonGradientTo: '#f97316',
              joinButtonText: '#ffffff'
            },
            game: {
              // Header
              headerBackground: '#fef7ff',
              headerBackgroundOpacity: 0.9,
              headerText: '#7c3aed',
              headerBorder: '#e2e8f0',
              guessesText: '#64748b',
              guessesOpacity: 0.8,

              // Surface/Answer slots
              surfaceBackground: '#ffffff',
              surfaceBackgroundOpacity: 0.9,
              filledSlotBackground: '#ec489920',
              filledSlotBackgroundOpacity: 0.5,
              text: '#1e293b',
              border: '#e2e8f0',
              rankNumberColor: '#06b6d4',
              rankNumberOpacity: 0.8,
              contextText: '#64748b',
              contextOpacity: 0.8,

              // Input
              inputBackground: '#ffffff',
              inputBackgroundOpacity: 0.9,
              inputText: '#1e293b',
              inputBorder: '#e2e8f0',
              inputPlaceholder: '#94a3b8',

              // Keyboard
              keyboardBackground: '#fef7ff',
              keyboardOpacity: 0.9,
              keyboardButtonBackground: '#f8fafc',
              keyboardButtonOpacity: 1,
              keyboardText: '#1e293b',
              keyboardBorder: '#e2e8f0',

              // Animations
              checkingColor: '#f59e0b',
              successColor: '#10b981'
            }
          },
          typography: {
            primaryFont: 'Quicksand',
            fontWeight: 'normal'
          }
        };

        // Add Arcade preset for listmaster game
        presets.arcade = {
          colors: {
            background: {
              gradient: {
                start: '#1E40AF',
                end: '#7C2D12'
              }
            },
            cardBackground: '#000000',
            cardOpacity: 0.9,
            titleText: '#FFFF00',
            subtitleText: '#00FF00',
            buttonBackground: '#FF0000',
            buttonText: '#FFFFFF',
            headlineText: '#FFFF00',
            subheadlineText: '#00FF00',
            results: {
              cardBackground: '#000000',
              cardBackgroundOpacity: 0.9,
              scoreLabelText: '#FFFF00',
              scoreValueText: '#FFFFFF',
              joinButtonGradientFrom: '#FF0000',
              joinButtonGradientTo: '#FF4000',
              joinButtonText: '#FFFFFF'
            },
            endCard: {
              background: '#000000',
              backgroundOpacity: 0.9,
              playAgainBackground: '#FF0000',
              playAgainText: '#FFFFFF',
              shareBackground: '#00FF00',
              shareText: '#000000',
              ctaButtonBackground: '#FFFF00',
              ctaButtonText: '#000000',
              ctaButtonOpacity: 1
            },
            leaderboard: {
              background: '#000000',
              backgroundOpacity: 0.9,
              leaderboardTitleText: '#FFFF00',
              rankText: '#00FF00',
              nameText: '#FFFFFF',
              scoreText: '#FF0000',
              userBackground: '#1E40AF',
              tabPrimary: '#FF0000',
              tabSecondary: '#00FF00',
              joinButtonGradientFrom: '#FF0000',
              joinButtonGradientTo: '#FF4000',
              joinButtonText: '#FFFFFF'
            },
            game: {
              // Header
              headerBackground: '#000000',
              headerBackgroundOpacity: 1,
              headerText: '#FFFF00',
              headerBorder: '#0000FF',
              guessesText: '#FFFFFF',
              guessesOpacity: 1,

              // Surface/Answer slots
              surfaceBackground: '#000000',
              surfaceBackgroundOpacity: 1,
              filledSlotBackground: '#FF0000',
              filledSlotBackgroundOpacity: 1,
              text: '#FFFFFF',
              border: '#0000FF',
              rankNumberColor: '#FFFF00',
              rankNumberOpacity: 1,
              contextText: '#00FF00',
              contextOpacity: 1,

              // Input
              inputBackground: '#000000',
              inputBackgroundOpacity: 1,
              inputText: '#FFFFFF',
              inputBorder: '#0000FF',
              inputPlaceholder: '#00FF00',

              // Keyboard
              keyboardBackground: '#000000',
              keyboardOpacity: 1,
              keyboardButtonBackground: '#1E40AF',
              keyboardButtonOpacity: 1,
              keyboardText: '#FFFFFF',
              keyboardBorder: '#0000FF',

              // Animations
              checkingColor: '#FF0000',
              successColor: '#FFFF00'
            }
          },
          typography: {
            primaryFont: 'Russo One',
            fontWeight: 'normal'
          }
        };

        // Add Neon preset specifically for listmaster game
        presets.neon = {
          colors: {
            background: {
              gradient: {
                start: '#0F0C29',
                end: '#24243e'
              }
            },
            cardBackground: '#1a1a2e',
            cardOpacity: 0.9,
            titleText: '#00f5ff',
            subtitleText: '#ff0080',
            buttonBackground: '#ff0080',
            buttonText: '#ffffff',
            headlineText: '#00f5ff',
            subheadlineText: '#ff0080',
            results: {
              cardBackground: '#1a1a2e',
              cardBackgroundOpacity: 0.9,
              scoreLabelText: '#00f5ff',
              scoreValueText: '#ffffff',
              joinButtonGradientFrom: '#ff0080',
              joinButtonGradientTo: '#ff1090',
              joinButtonText: '#ffffff'
            },
            endCard: {
              background: '#1a1a2e',
              backgroundOpacity: 0.9,
              playAgainBackground: '#ff0080',
              playAgainText: '#ffffff',
              shareBackground: '#00f5ff',
              shareText: '#000000',
              ctaButtonBackground: '#ff0080',
              ctaButtonText: '#ffffff',
              ctaButtonOpacity: 1
            },
            leaderboard: {
              background: '#1a1a2e',
              backgroundOpacity: 0.9,
              leaderboardTitleText: '#00f5ff',
              rankText: '#ff0080',
              nameText: '#ffffff',
              scoreText: '#00f5ff',
              userBackground: '#2a2a3e',
              tabPrimary: '#ff0080',
              tabSecondary: '#666699',
              joinButtonGradientFrom: '#ff0080',
              joinButtonGradientTo: '#ff1090',
              joinButtonText: '#ffffff'
            },
            game: {
              // Header
              headerBackground: '#0a0a1a',
              headerBackgroundOpacity: 1,
              headerText: '#00f5ff',
              headerBorder: '#ff0080',
              guessesText: '#ffffff',
              guessesOpacity: 0.8,

              // Surface/Answer slots
              surfaceBackground: '#1a1a2e',
              surfaceBackgroundOpacity: 0.95,
              filledSlotBackground: '#ff0080',
              filledSlotBackgroundOpacity: 0.3,
              text: '#ffffff',
              border: '#00f5ff',
              rankNumberColor: '#ff0080',
              rankNumberOpacity: 1,
              contextText: '#00f5ff',
              contextOpacity: 0.8,

              // Input
              inputBackground: '#1a1a2e',
              inputBackgroundOpacity: 0.95,
              inputText: '#ffffff',
              inputBorder: '#00f5ff',
              inputPlaceholder: '#666699',

              // Keyboard
              keyboardBackground: '#0a0a1a',
              keyboardOpacity: 0.95,
              keyboardButtonBackground: '#1a1a2e',
              keyboardButtonOpacity: 1,
              keyboardText: '#00f5ff',
              keyboardBorder: '#ff0080',

              // Animations
              checkingColor: '#ff0080',
              successColor: '#00f5ff',
              checkingAnimationBackground: '#ff0080'
            }
          },
          typography: {
            primaryFont: 'Orbitron',
            fontWeight: 'bold'
          }
        };
      } else if (gameType === 'snake') {
        // Add Monochrome preset for snake game
        presets.monochrome = {
          colors: {
            background: {
              gradient: {
                start: '#ffffff',
                end: '#f8f9fa'
              }
            },
            cardBackground: '#ffffff',
            cardOpacity: 1,
            titleText: '#000000',
            subtitleText: '#6c757d',
            buttonBackground: '#000000',
            buttonText: '#ffffff',
            headlineText: '#000000',
            subheadlineText: '#6c757d',
            results: {
              cardBackground: '#ffffff',
              cardBackgroundOpacity: 1,
              scoreLabelText: '#6c757d',
              scoreValueText: '#000000',
              joinButtonGradientFrom: '#000000',
              joinButtonGradientTo: '#343a40',
              joinButtonText: '#ffffff'
            },
            endCard: {
              background: '#ffffff',
              backgroundOpacity: 1,
              playAgainBackground: '#000000',
              playAgainText: '#ffffff',
              shareBackground: '#6c757d',
              shareText: '#ffffff',
              ctaButtonBackground: '#343a40',
              ctaButtonText: '#ffffff',
              ctaButtonOpacity: 1
            },
            leaderboard: {
              background: '#ffffff',
              backgroundOpacity: 1,
              leaderboardTitleText: '#000000',
              rankText: '#6c757d',
              nameText: '#000000',
              scoreText: '#343a40',
              userBackground: '#f8f9fa',
              tabPrimary: '#000000',
              tabSecondary: '#adb5bd',
              joinButtonGradientFrom: '#000000',
              joinButtonGradientTo: '#343a40',
              joinButtonText: '#ffffff'
            },
            game: {
              canvasColor: '#ffffff',
              snakeColor: '#000000',
              foodColor: '#6c757d',
              controlButtonColor: '#ffffff',
              controlButtonOpacity: 1,
              controlIconColor: '#000000',
              scoreText: '#000000'
            }
          },
          typography: {
            primaryFont: 'Inter',
            fontWeight: 'normal'
          }
        };

        // Add Pastel Dreams preset for snake game
        presets.pastel = {
          colors: {
            background: {
              gradient: {
                start: '#fef7ff',
                end: '#e0f2fe'
              }
            },
            cardBackground: '#ffffff',
            cardOpacity: 0.85,
            titleText: '#7c3aed',
            subtitleText: '#0ea5e9',
            buttonBackground: '#ec4899',
            buttonText: '#ffffff',
            headlineText: '#7c3aed',
            subheadlineText: '#0ea5e9',
            results: {
              cardBackground: '#ffffff',
              cardBackgroundOpacity: 0.85,
              scoreLabelText: '#0ea5e9',
              scoreValueText: '#1e293b',
              joinButtonGradientFrom: '#ec4899',
              joinButtonGradientTo: '#f97316',
              joinButtonText: '#ffffff'
            },
            endCard: {
              background: '#ffffff',
              backgroundOpacity: 0.85,
              playAgainBackground: '#ec4899',
              playAgainText: '#ffffff',
              shareBackground: '#06b6d4',
              shareText: '#ffffff',
              ctaButtonBackground: '#f59e0b',
              ctaButtonText: '#ffffff',
              ctaButtonOpacity: 1
            },
            leaderboard: {
              background: '#ffffff',
              backgroundOpacity: 0.85,
              leaderboardTitleText: '#7c3aed',
              rankText: '#06b6d4',
              nameText: '#1e293b',
              scoreText: '#dc2626',
              userBackground: '#f1f5f9',
              tabPrimary: '#ec4899',
              tabSecondary: '#94a3b8',
              joinButtonGradientFrom: '#ec4899',
              joinButtonGradientTo: '#f97316',
              joinButtonText: '#ffffff'
            },
            game: {
              canvasColor: '#f8fafc',
              snakeColor: '#10b981',
              foodColor: '#f59e0b',
              controlButtonColor: '#ffffff',
              controlButtonOpacity: 0.8,
              controlIconColor: '#ec4899',
              scoreText: '#7c3aed'
            }
          },
          typography: {
            primaryFont: 'Quicksand',
            fontWeight: 'normal'
          }
        };

        // Add Arcade preset for snake game
        presets.arcade = {
          colors: {
            background: {
              gradient: {
                start: '#1E40AF',
                end: '#7C2D12'
              }
            },
            cardBackground: '#000000',
            cardOpacity: 0.9,
            titleText: '#FFFF00',
            subtitleText: '#00FF00',
            buttonBackground: '#FF0000',
            buttonText: '#FFFFFF',
            headlineText: '#FFFF00',
            subheadlineText: '#00FF00',
            results: {
              cardBackground: '#000000',
              cardBackgroundOpacity: 0.9,
              scoreLabelText: '#FFFF00',
              scoreValueText: '#FFFFFF',
              joinButtonGradientFrom: '#FF0000',
              joinButtonGradientTo: '#FF4000',
              joinButtonText: '#FFFFFF'
            },
            endCard: {
              background: '#000000',
              backgroundOpacity: 0.9,
              playAgainBackground: '#FF0000',
              playAgainText: '#FFFFFF',
              shareBackground: '#00FF00',
              shareText: '#000000',
              ctaButtonBackground: '#FFFF00',
              ctaButtonText: '#000000',
              ctaButtonOpacity: 1
            },
            leaderboard: {
              background: '#000000',
              backgroundOpacity: 0.9,
              leaderboardTitleText: '#FFFF00',
              rankText: '#00FF00',
              nameText: '#FFFFFF',
              scoreText: '#FF0000',
              userBackground: '#1E40AF',
              tabPrimary: '#FF0000',
              tabSecondary: '#00FF00',
              joinButtonGradientFrom: '#FF0000',
              joinButtonGradientTo: '#FF4000',
              joinButtonText: '#FFFFFF'
            },
            game: {
              canvasColor: '#000000',
              snakeColor: '#00FF00',
              foodColor: '#FF0000',
              controlButtonColor: '#000000',
              controlButtonOpacity: 0.9,
              controlIconColor: '#FFFF00',
              scoreText: '#FFFFFF'
            }
          },
          typography: {
            primaryFont: 'Russo One',
            fontWeight: 'normal'
          }
        };

        // Add Neon preset for snake game
        presets.neon = {
          colors: {
            background: {
              gradient: {
                start: '#0F0C29',
                end: '#24243e'
              }
            },
            cardBackground: '#1a1a2e',
            cardOpacity: 0.9,
            titleText: '#00f5ff',
            subtitleText: '#ff0080',
            buttonBackground: '#ff0080',
            buttonText: '#ffffff',
            headlineText: '#00f5ff',
            subheadlineText: '#ff0080',
            results: {
              cardBackground: '#1a1a2e',
              cardBackgroundOpacity: 0.9,
              scoreLabelText: '#00f5ff',
              scoreValueText: '#ffffff',
              joinButtonGradientFrom: '#ff0080',
              joinButtonGradientTo: '#ff1090',
              joinButtonText: '#ffffff'
            },
            endCard: {
              background: '#1a1a2e',
              backgroundOpacity: 0.9,
              playAgainBackground: '#ff0080',
              playAgainText: '#ffffff',
              shareBackground: '#00f5ff',
              shareText: '#000000',
              ctaButtonBackground: '#ff0080',
              ctaButtonText: '#ffffff',
              ctaButtonOpacity: 1
            },
            leaderboard: {
              background: '#1a1a2e',
              backgroundOpacity: 0.9,
              leaderboardTitleText: '#00f5ff',
              rankText: '#ff0080',
              nameText: '#ffffff',
              scoreText: '#00f5ff',
              userBackground: '#2a2a3e',
              tabPrimary: '#ff0080',
              tabSecondary: '#666699',
              joinButtonGradientFrom: '#ff0080',
              joinButtonGradientTo: '#ff1090',
              joinButtonText: '#ffffff'
            },
            game: {
              canvasColor: '#0a0a1a',
              snakeColor: '#00f5ff',
              foodColor: '#ff0080',
              controlButtonColor: '#1a1a2e',
              controlButtonOpacity: 0.9,
              controlIconColor: '#00f5ff',
              scoreText: '#ffffff'
            }
          },
          typography: {
            primaryFont: 'Orbitron',
            fontWeight: 'bold'
          }
        };
      } else if (gameType === 'hint') {
        // Add Monochrome preset for hint game
        presets.monochrome = {
          colors: {
            background: {
              gradient: {
                start: '#ffffff',
                end: '#f8f9fa'
              }
            },
            cardBackground: '#ffffff',
            cardOpacity: 1,
            titleText: '#000000',
            subtitleText: '#6c757d',
            buttonBackground: '#000000',
            buttonText: '#ffffff',
            headlineText: '#000000',
            subheadlineText: '#6c757d',
            results: {
              cardBackground: '#ffffff',
              cardBackgroundOpacity: 1,
              scoreLabelText: '#6c757d',
              scoreValueText: '#000000',
              joinButtonGradientFrom: '#000000',
              joinButtonGradientTo: '#343a40',
              joinButtonText: '#ffffff'
            },
            endCard: {
              background: '#ffffff',
              backgroundOpacity: 1,
              playAgainBackground: '#000000',
              playAgainText: '#ffffff',
              shareBackground: '#6c757d',
              shareText: '#ffffff',
              ctaButtonBackground: '#343a40',
              ctaButtonText: '#ffffff',
              ctaButtonOpacity: 1
            },
            leaderboard: {
              background: '#ffffff',
              backgroundOpacity: 1,
              leaderboardTitleText: '#000000',
              rankText: '#6c757d',
              nameText: '#000000',
              scoreText: '#343a40',
              userBackground: '#f8f9fa',
              tabPrimary: '#000000',
              tabSecondary: '#adb5bd',
              joinButtonGradientFrom: '#000000',
              joinButtonGradientTo: '#343a40',
              joinButtonText: '#ffffff'
            },
            game: {
              // Header
              headerBackground: '#ffffff',
              headerBackgroundOpacity: 1,
              headerText: '#000000',
              headerBorder: '#e9ecef',
              guessesText: '#6c757d',
              guessesOpacity: 1,

              // Surface/Answer slots
              surfaceBackground: '#ffffff',
              surfaceBackgroundOpacity: 1,
              filledSlotBackground: '#000000',
              filledSlotBackgroundOpacity: 1,
              text: '#000000',
              border: '#e9ecef',
              rankNumberColor: '#6c757d',
              rankNumberOpacity: 1,
              contextText: '#6c757d',
              contextOpacity: 1,

              // Input
              inputBackground: '#ffffff',
              inputBackgroundOpacity: 1,
              inputText: '#000000',
              inputBorder: '#e9ecef',
              inputPlaceholder: '#adb5bd',

              // Keyboard
              keyboardBackground: '#f8f9fa',
              keyboardOpacity: 1,
              keyboardButtonBackground: '#ffffff',
              keyboardButtonOpacity: 1,
              keyboardText: '#000000',
              keyboardBorder: '#e9ecef',

              // Animations
              checkingColor: '#6c757d',
              successColor: '#000000',
              checkingAnimationBackground: '#6c757d',

              // Hint specific
              hintBackground: '#ffffff',
              hintBackgroundOpacity: 1,
              hintText: '#000000',
              hintBorder: '#e9ecef',
              hintIconColor: '#000000',
              resultBackground: '#ffffff',
              resultBackgroundOpacity: 1,
              resultText: '#000000',
              resultHoverBackground: '#f8f9fa',
              skipButtonBackground: '#000000',
              skipButtonBackgroundOpacity: 0.1,
              skipButtonText: '#000000',
              skipButtonHoverBackground: '#00000020',
              giveUpText: '#000000',
              giveUpBackground: '#00000010',
              giveUpHoverBackground: '#00000020',
              searchButtonBackground: '#000000',
              searchButtonText: '#ffffff',
              searchButtonBorder: '#000000'
            }
          },
          typography: {
            primaryFont: 'Inter',
            fontWeight: 'normal'
          }
        };

        // Add Pastel Dreams preset for hint game
        presets.pastel = {
          colors: {
            background: {
              gradient: {
                start: '#fef7ff',
                end: '#e0f2fe'
              }
            },
            cardBackground: '#ffffff',
            cardOpacity: 0.85,
            titleText: '#7c3aed',
            subtitleText: '#0ea5e9',
            buttonBackground: '#ec4899',
            buttonText: '#ffffff',
            headlineText: '#7c3aed',
            subheadlineText: '#0ea5e9',
            results: {
              cardBackground: '#ffffff',
              cardBackgroundOpacity: 0.85,
              scoreLabelText: '#0ea5e9',
              scoreValueText: '#1e293b',
              joinButtonGradientFrom: '#ec4899',
              joinButtonGradientTo: '#f97316',
              joinButtonText: '#ffffff'
            },
            endCard: {
              background: '#ffffff',
              backgroundOpacity: 0.85,
              playAgainBackground: '#ec4899',
              playAgainText: '#ffffff',
              shareBackground: '#06b6d4',
              shareText: '#ffffff',
              ctaButtonBackground: '#f59e0b',
              ctaButtonText: '#ffffff',
              ctaButtonOpacity: 1
            },
            leaderboard: {
              background: '#ffffff',
              backgroundOpacity: 0.85,
              leaderboardTitleText: '#7c3aed',
              rankText: '#06b6d4',
              nameText: '#1e293b',
              scoreText: '#dc2626',
              userBackground: '#f1f5f9',
              tabPrimary: '#ec4899',
              tabSecondary: '#94a3b8',
              joinButtonGradientFrom: '#ec4899',
              joinButtonGradientTo: '#f97316',
              joinButtonText: '#ffffff'
            },
            game: {
              // Header
              headerBackground: '#fef7ff',
              headerBackgroundOpacity: 0.9,
              headerText: '#7c3aed',
              headerBorder: '#e2e8f0',
              guessesText: '#64748b',
              guessesOpacity: 0.8,

              // Surface/Answer slots
              surfaceBackground: '#ffffff',
              surfaceBackgroundOpacity: 0.9,
              filledSlotBackground: '#10b98120',
              filledSlotBackgroundOpacity: 0.5,
              text: '#1e293b',
              border: '#e2e8f0',
              rankNumberColor: '#06b6d4',
              rankNumberOpacity: 0.8,
              contextText: '#64748b',
              contextOpacity: 0.8,

              // Input
              inputBackground: '#ffffff',
              inputBackgroundOpacity: 0.9,
              inputText: '#1e293b',
              inputBorder: '#e2e8f0',
              inputPlaceholder: '#94a3b8',

              // Keyboard
              keyboardBackground: '#fef7ff',
              keyboardOpacity: 0.9,
              keyboardButtonBackground: '#f8fafc',
              keyboardButtonOpacity: 1,
              keyboardText: '#1e293b',
              keyboardBorder: '#e2e8f0',

              // Animations
              checkingColor: '#f59e0b',
              successColor: '#10b981',
              checkingAnimationBackground: '#f59e0b',

              // Hint specific
              hintBackground: '#ffffff',
              hintBackgroundOpacity: 0.9,
              hintText: '#7c3aed',
              hintBorder: '#e2e8f0',
              hintIconColor: '#7c3aed',
              resultBackground: '#ffffff',
              resultBackgroundOpacity: 0.9,
              resultText: '#1e293b',
              resultHoverBackground: '#f8fafc',
              skipButtonBackground: '#ec4899',
              skipButtonBackgroundOpacity: 0.1,
              skipButtonText: '#ec4899',
              skipButtonHoverBackground: '#ec489920',
              giveUpText: '#ec4899',
              giveUpBackground: '#ec489910',
              giveUpHoverBackground: '#ec489920',
              searchButtonBackground: '#7c3aed',
              searchButtonText: '#ffffff',
              searchButtonBorder: '#7c3aed'
            }
          },
          typography: {
            primaryFont: 'Quicksand',
            fontWeight: 'normal'
          }
        };

        // Add Arcade preset for hint game
        presets.arcade = {
          colors: {
            background: {
              gradient: {
                start: '#1E40AF',
                end: '#7C2D12'
              }
            },
            cardBackground: '#000000',
            cardOpacity: 0.9,
            titleText: '#FFFF00',
            subtitleText: '#00FF00',
            buttonBackground: '#FF0000',
            buttonText: '#FFFFFF',
            headlineText: '#FFFF00',
            subheadlineText: '#00FF00',
            results: {
              cardBackground: '#000000',
              cardBackgroundOpacity: 0.9,
              scoreLabelText: '#FFFF00',
              scoreValueText: '#FFFFFF',
              joinButtonGradientFrom: '#FF0000',
              joinButtonGradientTo: '#FF4000',
              joinButtonText: '#FFFFFF'
            },
            endCard: {
              background: '#000000',
              backgroundOpacity: 0.9,
              playAgainBackground: '#FF0000',
              playAgainText: '#FFFFFF',
              shareBackground: '#00FF00',
              shareText: '#000000',
              ctaButtonBackground: '#FFFF00',
              ctaButtonText: '#000000',
              ctaButtonOpacity: 1
            },
            leaderboard: {
              background: '#000000',
              backgroundOpacity: 0.9,
              leaderboardTitleText: '#FFFF00',
              rankText: '#00FF00',
              nameText: '#FFFFFF',
              scoreText: '#FF0000',
              userBackground: '#1E40AF',
              tabPrimary: '#FF0000',
              tabSecondary: '#00FF00',
              joinButtonGradientFrom: '#FF0000',
              joinButtonGradientTo: '#FF4000',
              joinButtonText: '#FFFFFF'
            },
            game: {
              // Header
              headerBackground: '#000000',
              headerBackgroundOpacity: 1,
              headerText: '#FFFF00',
              headerBorder: '#0000FF',
              guessesText: '#FFFFFF',
              guessesOpacity: 1,

              // Surface/Answer slots
              surfaceBackground: '#000000',
              surfaceBackgroundOpacity: 1,
              filledSlotBackground: '#FF0000',
              filledSlotBackgroundOpacity: 1,
              text: '#FFFFFF',
              border: '#0000FF',
              rankNumberColor: '#FFFF00',
              rankNumberOpacity: 1,
              contextText: '#00FF00',
              contextOpacity: 1,

              // Input
              inputBackground: '#000000',
              inputBackgroundOpacity: 1,
              inputText: '#FFFFFF',
              inputBorder: '#0000FF',
              inputPlaceholder: '#00FF00',

              // Keyboard
              keyboardBackground: '#000000',
              keyboardOpacity: 1,
              keyboardButtonBackground: '#1E40AF',
              keyboardButtonOpacity: 1,
              keyboardText: '#FFFFFF',
              keyboardBorder: '#0000FF',

              // Animations
              checkingColor: '#FF0000',
              successColor: '#FFFF00',
              checkingAnimationBackground: '#FF0000',

              // Hint specific
              hintBackground: '#000000',
              hintBackgroundOpacity: 1,
              hintText: '#FFFF00',
              hintBorder: '#0000FF',
              hintIconColor: '#FFFF00',
              resultBackground: '#000000',
              resultBackgroundOpacity: 1,
              resultText: '#FFFFFF',
              resultHoverBackground: '#1E40AF',
              skipButtonBackground: '#FF0000',
              skipButtonBackgroundOpacity: 0.3,
              skipButtonText: '#FF0000',
              skipButtonHoverBackground: '#FF000040',
              giveUpText: '#FF0000',
              giveUpBackground: '#FF000020',
              giveUpHoverBackground: '#FF000040',
              searchButtonBackground: '#FF0000',
              searchButtonText: '#FFFFFF',
              searchButtonBorder: '#0000FF'
            }
          },
          typography: {
            primaryFont: 'Russo One',
            fontWeight: 'normal'
          }
        };

        // Add Neon preset for hint game
        presets.neon = {
          colors: {
            background: {
              gradient: {
                start: '#0F0C29',
                end: '#24243e'
              }
            },
            cardBackground: '#1a1a2e',
            cardOpacity: 0.9,
            titleText: '#00f5ff',
            subtitleText: '#ff0080',
            buttonBackground: '#ff0080',
            buttonText: '#ffffff',
            headlineText: '#00f5ff',
            subheadlineText: '#ff0080',
            results: {
              cardBackground: '#1a1a2e',
              cardBackgroundOpacity: 0.9,
              scoreLabelText: '#00f5ff',
              scoreValueText: '#ffffff',
              joinButtonGradientFrom: '#ff0080',
              joinButtonGradientTo: '#ff1090',
              joinButtonText: '#ffffff'
            },
            endCard: {
              background: '#1a1a2e',
              backgroundOpacity: 0.9,
              playAgainBackground: '#ff0080',
              playAgainText: '#ffffff',
              shareBackground: '#00f5ff',
              shareText: '#000000',
              ctaButtonBackground: '#ff0080',
              ctaButtonText: '#ffffff',
              ctaButtonOpacity: 1
            },
            leaderboard: {
              background: '#1a1a2e',
              backgroundOpacity: 0.9,
              leaderboardTitleText: '#00f5ff',
              rankText: '#ff0080',
              nameText: '#ffffff',
              scoreText: '#00f5ff',
              userBackground: '#2a2a3e',
              tabPrimary: '#ff0080',
              tabSecondary: '#666699',
              joinButtonGradientFrom: '#ff0080',
              joinButtonGradientTo: '#ff1090',
              joinButtonText: '#ffffff'
            },
            game: {
              // Header
              headerBackground: '#0a0a1a',
              headerBackgroundOpacity: 1,
              headerText: '#00f5ff',
              headerBorder: '#ff0080',
              guessesText: '#ffffff',
              guessesOpacity: 0.8,

              // Surface/Answer slots
              surfaceBackground: '#1a1a2e',
              surfaceBackgroundOpacity: 0.95,
              filledSlotBackground: '#ff0080',
              filledSlotBackgroundOpacity: 0.3,
              text: '#ffffff',
              border: '#00f5ff',
              rankNumberColor: '#ff0080',
              rankNumberOpacity: 1,
              contextText: '#00f5ff',
              contextOpacity: 0.8,

              // Input
              inputBackground: '#1a1a2e',
              inputBackgroundOpacity: 0.95,
              inputText: '#ffffff',
              inputBorder: '#00f5ff',
              inputPlaceholder: '#666699',

              // Keyboard
              keyboardBackground: '#0a0a1a',
              keyboardOpacity: 0.95,
              keyboardButtonBackground: '#1a1a2e',
              keyboardButtonOpacity: 1,
              keyboardText: '#00f5ff',
              keyboardBorder: '#ff0080',

              // Animations
              checkingColor: '#ff0080',
              successColor: '#00f5ff',
              checkingAnimationBackground: '#ff0080',

              // Hint specific
              hintBackground: '#1a1a2e',
              hintBackgroundOpacity: 1,
              hintText: '#00f5ff',
              hintBorder: '#ff0080',
              hintIconColor: '#00f5ff',
              resultBackground: '#1a1a2e',
              resultBackgroundOpacity: 1,
              resultText: '#ffffff',
              resultHoverBackground: '#2a2a3e',
              skipButtonBackground: '#ff0080',
              skipButtonBackgroundOpacity: 0.3,
              skipButtonText: '#ff0080',
              skipButtonHoverBackground: '#ff008040',
              giveUpText: '#ff0080',
              giveUpBackground: '#ff008020',
              giveUpHoverBackground: '#ff008040',
              searchButtonBackground: '#ff0080',
              searchButtonText: '#ffffff',
              searchButtonBorder: '#00f5ff'
            }
          },
          typography: {
            primaryFont: 'Orbitron',
            fontWeight: 'bold'
          }
        };
      } else if (gameType === 'mapselect') {
        // Add Monochrome preset for mapselect game
        presets.monochrome = {
          colors: {
            background: {
              gradient: {
                start: '#ffffff',
                end: '#f8f9fa'
              }
            },
            cardBackground: '#ffffff',
            cardOpacity: 1,
            titleText: '#000000',
            subtitleText: '#6c757d',
            buttonBackground: '#000000',
            buttonText: '#ffffff',
            headlineText: '#000000',
            subheadlineText: '#6c757d',
            results: {
              cardBackground: '#ffffff',
              cardBackgroundOpacity: 1,
              scoreLabelText: '#6c757d',
              scoreValueText: '#000000',
              joinButtonGradientFrom: '#000000',
              joinButtonGradientTo: '#343a40',
              joinButtonText: '#ffffff'
            },
            endCard: {
              background: '#ffffff',
              backgroundOpacity: 1,
              playAgainBackground: '#000000',
              playAgainText: '#ffffff',
              shareBackground: '#6c757d',
              shareText: '#ffffff',
              ctaButtonBackground: '#343a40',
              ctaButtonText: '#ffffff',
              ctaButtonOpacity: 1
            },
            leaderboard: {
              background: '#ffffff',
              backgroundOpacity: 1,
              leaderboardTitleText: '#000000',
              rankText: '#6c757d',
              nameText: '#000000',
              scoreText: '#343a40',
              userBackground: '#f8f9fa',
              tabPrimary: '#000000',
              tabSecondary: '#adb5bd',
              joinButtonGradientFrom: '#000000',
              joinButtonGradientTo: '#343a40',
              joinButtonText: '#ffffff'
            },
            game: {
              inputBackground: '#ffffff',
              inputBackgroundOpacity: 1,
              searchButtonBackground: '#000000',
              searchButtonOpacity: 1,
              searchButtonBorder: '#000000',
              searchButtonText: '#ffffff',
              inputText: '#000000',
              inputBorder: '#e9ecef',
              inputPlaceholder: '#adb5bd',
              resultsBackground: '#ffffff',
              resultsBackgroundOpacity: 1,
              resultsText: '#000000',
              resultsHoverBackground: '#f8f9fa',
              keyboardBackground: '#f8f9fa',
              keyboardOpacity: 1,
              keyboardButtonBackground: '#ffffff',
              keyboardButtonOpacity: 1,
              keyboardText: '#000000',
              keyboardBorder: '#e9ecef'
            }
          },
          typography: {
            primaryFont: 'Inter',
            fontWeight: 'normal'
          }
        };

        // Add Pastel Dreams preset for mapselect game
        presets.pastel = {
          colors: {
            background: {
              gradient: {
                start: '#fef7ff',
                end: '#e0f2fe'
              }
            },
            cardBackground: '#ffffff',
            cardOpacity: 0.85,
            titleText: '#7c3aed',
            subtitleText: '#0ea5e9',
            buttonBackground: '#ec4899',
            buttonText: '#ffffff',
            headlineText: '#7c3aed',
            subheadlineText: '#0ea5e9',
            results: {
              cardBackground: '#ffffff',
              cardBackgroundOpacity: 0.85,
              scoreLabelText: '#0ea5e9',
              scoreValueText: '#1e293b',
              joinButtonGradientFrom: '#ec4899',
              joinButtonGradientTo: '#f97316',
              joinButtonText: '#ffffff'
            },
            endCard: {
              background: '#ffffff',
              backgroundOpacity: 0.85,
              playAgainBackground: '#ec4899',
              playAgainText: '#ffffff',
              shareBackground: '#06b6d4',
              shareText: '#ffffff',
              ctaButtonBackground: '#f59e0b',
              ctaButtonText: '#ffffff',
              ctaButtonOpacity: 1
            },
            leaderboard: {
              background: '#ffffff',
              backgroundOpacity: 0.85,
              leaderboardTitleText: '#7c3aed',
              rankText: '#06b6d4',
              nameText: '#1e293b',
              scoreText: '#dc2626',
              userBackground: '#f1f5f9',
              tabPrimary: '#ec4899',
              tabSecondary: '#94a3b8',
              joinButtonGradientFrom: '#ec4899',
              joinButtonGradientTo: '#f97316',
              joinButtonText: '#ffffff'
            },
            game: {
              inputBackground: '#ffffff',
              inputBackgroundOpacity: 0.9,
              searchButtonBackground: '#7c3aed',
              searchButtonOpacity: 1,
              searchButtonBorder: '#7c3aed',
              searchButtonText: '#ffffff',
              inputText: '#1e293b',
              inputBorder: '#e2e8f0',
              inputPlaceholder: '#94a3b8',
              resultsBackground: '#ffffff',
              resultsBackgroundOpacity: 0.9,
              resultsText: '#1e293b',
              resultsHoverBackground: '#f8fafc',
              keyboardBackground: '#fef7ff',
              keyboardOpacity: 0.9,
              keyboardButtonBackground: '#f8fafc',
              keyboardButtonOpacity: 1,
              keyboardText: '#1e293b',
              keyboardBorder: '#e2e8f0'
            }
          },
          typography: {
            primaryFont: 'Quicksand',
            fontWeight: 'normal'
          }
        };

        // Add Arcade preset for mapselect game
        presets.arcade = {
          colors: {
            background: {
              gradient: {
                start: '#1E40AF',
                end: '#7C2D12'
              }
            },
            cardBackground: '#000000',
            cardOpacity: 0.9,
            titleText: '#FFFF00',
            subtitleText: '#00FF00',
            buttonBackground: '#FF0000',
            buttonText: '#FFFFFF',
            headlineText: '#FFFF00',
            subheadlineText: '#00FF00',
            results: {
              cardBackground: '#000000',
              cardBackgroundOpacity: 0.9,
              scoreLabelText: '#FFFF00',
              scoreValueText: '#FFFFFF',
              joinButtonGradientFrom: '#FF0000',
              joinButtonGradientTo: '#FF4000',
              joinButtonText: '#FFFFFF'
            },
            endCard: {
              background: '#000000',
              backgroundOpacity: 0.9,
              playAgainBackground: '#FF0000',
              playAgainText: '#FFFFFF',
              shareBackground: '#00FF00',
              shareText: '#000000',
              ctaButtonBackground: '#FFFF00',
              ctaButtonText: '#000000',
              ctaButtonOpacity: 1
            },
            leaderboard: {
              background: '#000000',
              backgroundOpacity: 0.9,
              leaderboardTitleText: '#FFFF00',
              rankText: '#00FF00',
              nameText: '#FFFFFF',
              scoreText: '#FF0000',
              userBackground: '#1E40AF',
              tabPrimary: '#FF0000',
              tabSecondary: '#00FF00',
              joinButtonGradientFrom: '#FF0000',
              joinButtonGradientTo: '#FF4000',
              joinButtonText: '#FFFFFF'
            },
            game: {
              inputBackground: '#000000',
              inputBackgroundOpacity: 0.9,
              searchButtonBackground: '#FF0000',
              searchButtonOpacity: 1,
              searchButtonBorder: '#0000FF',
              searchButtonText: '#FFFFFF',
              inputText: '#FFFFFF',
              inputBorder: '#0000FF',
              inputPlaceholder: '#00FF00',
              resultsBackground: '#000000',
              resultsBackgroundOpacity: 0.9,
              resultsText: '#FFFFFF',
              resultsHoverBackground: '#1E40AF',
              keyboardBackground: '#000000',
              keyboardOpacity: 0.9,
              keyboardButtonBackground: '#1E40AF',
              keyboardButtonOpacity: 1,
              keyboardText: '#FFFFFF',
              keyboardBorder: '#0000FF'
            }
          },
          typography: {
            primaryFont: 'Russo One',
            fontWeight: 'normal'
          }
        };

        // Add Neon preset for mapselect game
        presets.neon = {
          colors: {
            background: {
              gradient: {
                start: '#0F0C29',
                end: '#24243e'
              }
            },
            cardBackground: '#1a1a2e',
            cardOpacity: 0.9,
            titleText: '#00f5ff',
            subtitleText: '#ff0080',
            buttonBackground: '#ff0080',
            buttonText: '#ffffff',
            headlineText: '#00f5ff',
            subheadlineText: '#ff0080',
            results: {
              cardBackground: '#1a1a2e',
              cardBackgroundOpacity: 0.9,
              scoreLabelText: '#00f5ff',
              scoreValueText: '#ffffff',
              joinButtonGradientFrom: '#ff0080',
              joinButtonGradientTo: '#ff1090',
              joinButtonText: '#ffffff'
            },
            endCard: {
              background: '#1a1a2e',
              backgroundOpacity: 0.9,
              playAgainBackground: '#ff0080',
              playAgainText: '#ffffff',
              shareBackground: '#00f5ff',
              shareText: '#000000',
              ctaButtonBackground: '#ff0080',
              ctaButtonText: '#ffffff',
              ctaButtonOpacity: 1
            },
            leaderboard: {
              background: '#1a1a2e',
              backgroundOpacity: 0.9,
              leaderboardTitleText: '#00f5ff',
              rankText: '#ff0080',
              nameText: '#ffffff',
              scoreText: '#00f5ff',
              userBackground: '#2a2a3e',
              tabPrimary: '#ff0080',
              tabSecondary: '#666699',
              joinButtonGradientFrom: '#ff0080',
              joinButtonGradientTo: '#ff1090',
              joinButtonText: '#ffffff'
            },
            game: {
              inputBackground: '#1a1a2e',
              inputBackgroundOpacity: 0.9,
              searchButtonBackground: '#ff0080',
              searchButtonOpacity: 1,
              searchButtonBorder: '#00f5ff',
              searchButtonText: '#ffffff',
              inputText: '#ffffff',
              inputBorder: '#00f5ff',
              inputPlaceholder: '#666699',
              resultsBackground: '#1a1a2e',
              resultsBackgroundOpacity: 0.9,
              resultsText: '#ffffff',
              resultsHoverBackground: '#2a2a3e',
              keyboardBackground: '#0a0a1a',
              keyboardOpacity: 0.9,
              keyboardButtonBackground: '#1a1a2e',
              keyboardButtonOpacity: 1,
              keyboardText: '#00f5ff',
              keyboardBorder: '#ff0080'
            }
          },
          typography: {
            primaryFont: 'Orbitron',
            fontWeight: 'bold'
          }
        };
      } else if (gameType === 'quiz') {
        // Add Monochrome preset for quiz game
        presets.monochrome = {
          colors: {
            background: {
              gradient: {
                start: '#ffffff',
                end: '#f8f9fa'
              }
            },
            cardBackground: '#ffffff',
            cardOpacity: 1,
            titleText: '#000000',
            subtitleText: '#6c757d',
            buttonBackground: '#000000',
            buttonText: '#ffffff',
            headlineText: '#000000',
            subheadlineText: '#6c757d',
            results: {
              cardBackground: '#ffffff',
              cardBackgroundOpacity: 1,
              scoreLabelText: '#6c757d',
              scoreValueText: '#000000',
              joinButtonGradientFrom: '#000000',
              joinButtonGradientTo: '#343a40',
              joinButtonText: '#ffffff'
            },
            endCard: {
              background: '#ffffff',
              backgroundOpacity: 1,
              playAgainBackground: '#000000',
              playAgainText: '#ffffff',
              shareBackground: '#6c757d',
              shareText: '#ffffff',
              ctaButtonBackground: '#343a40',
              ctaButtonText: '#ffffff',
              ctaButtonOpacity: 1
            },
            leaderboard: {
              background: '#ffffff',
              backgroundOpacity: 1,
              leaderboardTitleText: '#000000',
              rankText: '#6c757d',
              nameText: '#000000',
              scoreText: '#343a40',
              userBackground: '#f8f9fa',
              tabPrimary: '#000000',
              tabSecondary: '#adb5bd',
              joinButtonGradientFrom: '#000000',
              joinButtonGradientTo: '#343a40',
              joinButtonText: '#ffffff'
            },
            game: {
              surface: {
                background: '#ffffff',
                opacity: 1,
                questionText: '#000000',
                questionBadgeBackground: '#000000',
                questionBadgeOpacity: 0.1,
                questionBadgeText: '#000000',
                answerBackground: '#f8f9fa',
                answerOpacity: 1,
                answerText: '#000000',
                hintText: '#6c757d',
                hintBackground: '#ffffff',
                hintBackgroundOpacity: 1,
                hintBorder: '#e9ecef',
                hintIconColor: '#6c757d'
              }
            }
          },
          typography: {
            primaryFont: 'Inter',
            fontWeight: 'normal'
          }
        };

        // Add Pastel Dreams preset for quiz game
        presets.pastel = {
          colors: {
            background: {
              gradient: {
                start: '#fef7ff',
                end: '#e0f2fe'
              }
            },
            cardBackground: '#ffffff',
            cardOpacity: 0.85,
            titleText: '#7c3aed',
            subtitleText: '#0ea5e9',
            buttonBackground: '#ec4899',
            buttonText: '#ffffff',
            headlineText: '#7c3aed',
            subheadlineText: '#0ea5e9',
            results: {
              cardBackground: '#ffffff',
              cardBackgroundOpacity: 0.85,
              scoreLabelText: '#0ea5e9',
              scoreValueText: '#1e293b',
              joinButtonGradientFrom: '#ec4899',
              joinButtonGradientTo: '#f97316',
              joinButtonText: '#ffffff'
            },
            endCard: {
              background: '#ffffff',
              backgroundOpacity: 0.85,
              playAgainBackground: '#ec4899',
              playAgainText: '#ffffff',
              shareBackground: '#06b6d4',
              shareText: '#ffffff',
              ctaButtonBackground: '#f59e0b',
              ctaButtonText: '#ffffff',
              ctaButtonOpacity: 1
            },
            leaderboard: {
              background: '#ffffff',
              backgroundOpacity: 0.85,
              leaderboardTitleText: '#7c3aed',
              rankText: '#06b6d4',
              nameText: '#1e293b',
              scoreText: '#dc2626',
              userBackground: '#f1f5f9',
              tabPrimary: '#ec4899',
              tabSecondary: '#94a3b8',
              joinButtonGradientFrom: '#ec4899',
              joinButtonGradientTo: '#f97316',
              joinButtonText: '#ffffff'
            },
            game: {
              surface: {
                background: '#ffffff',
                opacity: 0.9,
                questionText: '#1e293b',
                questionBadgeBackground: '#7c3aed',
                questionBadgeOpacity: 0.1,
                questionBadgeText: '#7c3aed',
                answerBackground: '#f8fafc',
                answerOpacity: 0.9,
                answerText: '#1e293b',
                hintText: '#06b6d4',
                hintBackground: '#ffffff',
                hintBackgroundOpacity: 0.9,
                hintBorder: '#e2e8f0',
                hintIconColor: '#06b6d4'
              }
            }
          },
          typography: {
            primaryFont: 'Quicksand',
            fontWeight: 'normal'
          }
        };

        // Add Arcade preset for quiz game
        presets.arcade = {
          colors: {
            background: {
              gradient: {
                start: '#1E40AF',
                end: '#7C2D12'
              }
            },
            cardBackground: '#000000',
            cardOpacity: 0.9,
            titleText: '#FFFF00',
            subtitleText: '#00FF00',
            buttonBackground: '#FF0000',
            buttonText: '#FFFFFF',
            headlineText: '#FFFF00',
            subheadlineText: '#00FF00',
            results: {
              cardBackground: '#000000',
              cardBackgroundOpacity: 0.9,
              scoreLabelText: '#FFFF00',
              scoreValueText: '#FFFFFF',
              joinButtonGradientFrom: '#FF0000',
              joinButtonGradientTo: '#FF4000',
              joinButtonText: '#FFFFFF'
            },
            endCard: {
              background: '#000000',
              backgroundOpacity: 0.9,
              playAgainBackground: '#FF0000',
              playAgainText: '#FFFFFF',
              shareBackground: '#00FF00',
              shareText: '#000000',
              ctaButtonBackground: '#FFFF00',
              ctaButtonText: '#000000',
              ctaButtonOpacity: 1
            },
            leaderboard: {
              background: '#000000',
              backgroundOpacity: 0.9,
              leaderboardTitleText: '#FFFF00',
              rankText: '#00FF00',
              nameText: '#FFFFFF',
              scoreText: '#FF0000',
              userBackground: '#1E40AF',
              tabPrimary: '#FF0000',
              tabSecondary: '#00FF00',
              joinButtonGradientFrom: '#FF0000',
              joinButtonGradientTo: '#FF4000',
              joinButtonText: '#FFFFFF'
            },
            game: {
              surface: {
                background: '#000000',
                opacity: 0.95,
                questionText: '#FFFFFF',
                questionBadgeBackground: '#FFFF00',
                questionBadgeOpacity: 0.3,
                questionBadgeText: '#FFFF00',
                answerBackground: '#1E40AF',
                answerOpacity: 0.9,
                answerText: '#FFFFFF',
                hintText: '#00FF00',
                hintBackground: '#000000',
                hintBackgroundOpacity: 1,
                hintBorder: '#00FF00',
                hintIconColor: '#00FF00'
              }
            }
          },
          typography: {
            primaryFont: 'Russo One',
            fontWeight: 'normal'
          }
        };

        // Add Neon preset for quiz game
        presets.neon = {
          colors: {
            background: {
              gradient: {
                start: '#0F0C29',
                end: '#24243e'
              }
            },
            cardBackground: '#1a1a2e',
            cardOpacity: 0.9,
            titleText: '#00f5ff',
            subtitleText: '#ff0080',
            buttonBackground: '#ff0080',
            buttonText: '#ffffff',
            headlineText: '#00f5ff',
            subheadlineText: '#ff0080',
            results: {
              cardBackground: '#1a1a2e',
              cardBackgroundOpacity: 0.9,
              scoreLabelText: '#00f5ff',
              scoreValueText: '#ffffff',
              joinButtonGradientFrom: '#ff0080',
              joinButtonGradientTo: '#ff1090',
              joinButtonText: '#ffffff'
            },
            endCard: {
              background: '#1a1a2e',
              backgroundOpacity: 0.9,
              playAgainBackground: '#ff0080',
              playAgainText: '#ffffff',
              shareBackground: '#00f5ff',
              shareText: '#000000',
              ctaButtonBackground: '#ff0080',
              ctaButtonText: '#ffffff',
              ctaButtonOpacity: 1
            },
            leaderboard: {
              background: '#1a1a2e',
              backgroundOpacity: 0.9,
              leaderboardTitleText: '#00f5ff',
              rankText: '#ff0080',
              nameText: '#ffffff',
              scoreText: '#00f5ff',
              userBackground: '#2a2a3e',
              tabPrimary: '#ff0080',
              tabSecondary: '#666699',
              joinButtonGradientFrom: '#ff0080',
              joinButtonGradientTo: '#ff1090',
              joinButtonText: '#ffffff'
            },
            game: {
              surface: {
                background: '#1a1a2e',
                opacity: 0.95,
                questionText: '#ffffff',
                questionBadgeBackground: '#ff0080',
                questionBadgeOpacity: 0.3,
                questionBadgeText: '#ff0080',
                answerBackground: '#0a0a1a',
                answerOpacity: 0.9,
                answerText: '#ffffff',
                hintText: '#00f5ff',
                hintBackground: '#1a1a2e',
                hintBackgroundOpacity: 1,
                hintBorder: '#00f5ff',
                hintIconColor: '#00f5ff'
              }
            }
          },
          typography: {
            primaryFont: 'Orbitron',
            fontWeight: 'bold'
          }
        };
      } else if (gameType === 'pindrop') {
        // Add Monochrome preset for pindrop game
        presets.monochrome = {
          colors: {
            background: {
              gradient: {
                start: '#ffffff',
                end: '#f8f9fa'
              }
            },
            cardBackground: '#ffffff',
            cardOpacity: 1,
            titleText: '#000000',
            subtitleText: '#6c757d',
            buttonBackground: '#000000',
            buttonText: '#ffffff',
            headlineText: '#000000',
            subheadlineText: '#6c757d',
            results: {
              cardBackground: '#ffffff',
              cardBackgroundOpacity: 1,
              scoreLabelText: '#6c757d',
              scoreValueText: '#000000',
              joinButtonGradientFrom: '#000000',
              joinButtonGradientTo: '#343a40',
              joinButtonText: '#ffffff'
            },
            endCard: {
              background: '#ffffff',
              backgroundOpacity: 1,
              playAgainBackground: '#000000',
              playAgainText: '#ffffff',
              shareBackground: '#6c757d',
              shareText: '#ffffff',
              ctaButtonBackground: '#343a40',
              ctaButtonText: '#ffffff',
              ctaButtonOpacity: 1
            },
            leaderboard: {
              background: '#ffffff',
              backgroundOpacity: 1,
              leaderboardTitleText: '#000000',
              rankText: '#6c757d',
              nameText: '#000000',
              scoreText: '#343a40',
              userBackground: '#f8f9fa',
              tabPrimary: '#000000',
              tabSecondary: '#adb5bd',
              joinButtonGradientFrom: '#000000',
              joinButtonGradientTo: '#343a40',
              joinButtonText: '#ffffff'
            },
            game: {
              topSection: '#ffffff',
              textColor: '#000000',
              topSectionOpacity: 1,
              polyline: '#6c757d',
              marker: '#000000'
            }
          },
          typography: {
            primaryFont: 'Inter',
            fontWeight: 'normal'
          }
        };

        // Add Pastel Dreams preset for pindrop game
        presets.pastel = {
          colors: {
            background: {
              gradient: {
                start: '#fef7ff',
                end: '#e0f2fe'
              }
            },
            cardBackground: '#ffffff',
            cardOpacity: 0.85,
            titleText: '#7c3aed',
            subtitleText: '#0ea5e9',
            buttonBackground: '#ec4899',
            buttonText: '#ffffff',
            headlineText: '#7c3aed',
            subheadlineText: '#0ea5e9',
            results: {
              cardBackground: '#ffffff',
              cardBackgroundOpacity: 0.85,
              scoreLabelText: '#0ea5e9',
              scoreValueText: '#1e293b',
              joinButtonGradientFrom: '#ec4899',
              joinButtonGradientTo: '#f97316',
              joinButtonText: '#ffffff'
            },
            endCard: {
              background: '#ffffff',
              backgroundOpacity: 0.85,
              playAgainBackground: '#ec4899',
              playAgainText: '#ffffff',
              shareBackground: '#06b6d4',
              shareText: '#ffffff',
              ctaButtonBackground: '#f59e0b',
              ctaButtonText: '#ffffff',
              ctaButtonOpacity: 1
            },
            leaderboard: {
              background: '#ffffff',
              backgroundOpacity: 0.85,
              leaderboardTitleText: '#7c3aed',
              rankText: '#06b6d4',
              nameText: '#1e293b',
              scoreText: '#dc2626',
              userBackground: '#f1f5f9',
              tabPrimary: '#ec4899',
              tabSecondary: '#94a3b8',
              joinButtonGradientFrom: '#ec4899',
              joinButtonGradientTo: '#f97316',
              joinButtonText: '#ffffff'
            },
            game: {
              topSection: '#fef7ff',
              textColor: '#7c3aed',
              topSectionOpacity: 0.9,
              polyline: '#06b6d4',
              marker: '#ec4899'
            }
          },
          typography: {
            primaryFont: 'Quicksand',
            fontWeight: 'normal'
          }
        };

        // Add Arcade preset for pindrop game
        presets.arcade = {
          colors: {
            background: {
              gradient: {
                start: '#1E40AF',
                end: '#7C2D12'
              }
            },
            cardBackground: '#000000',
            cardOpacity: 0.9,
            titleText: '#FFFF00',
            subtitleText: '#00FF00',
            buttonBackground: '#FF0000',
            buttonText: '#FFFFFF',
            headlineText: '#FFFF00',
            subheadlineText: '#00FF00',
            results: {
              cardBackground: '#000000',
              cardBackgroundOpacity: 0.9,
              scoreLabelText: '#FFFF00',
              scoreValueText: '#FFFFFF',
              joinButtonGradientFrom: '#FF0000',
              joinButtonGradientTo: '#FF4000',
              joinButtonText: '#FFFFFF'
            },
            endCard: {
              background: '#000000',
              backgroundOpacity: 0.9,
              playAgainBackground: '#FF0000',
              playAgainText: '#FFFFFF',
              shareBackground: '#00FF00',
              shareText: '#000000',
              ctaButtonBackground: '#FFFF00',
              ctaButtonText: '#000000',
              ctaButtonOpacity: 1
            },
            leaderboard: {
              background: '#000000',
              backgroundOpacity: 0.9,
              leaderboardTitleText: '#FFFF00',
              rankText: '#00FF00',
              nameText: '#FFFFFF',
              scoreText: '#FF0000',
              userBackground: '#1E40AF',
              tabPrimary: '#FF0000',
              tabSecondary: '#00FF00',
              joinButtonGradientFrom: '#FF0000',
              joinButtonGradientTo: '#FF4000',
              joinButtonText: '#FFFFFF'
            },
            game: {
              topSection: '#000000',
              textColor: '#FFFF00',
              topSectionOpacity: 0.95,
              polyline: '#00FF00',
              marker: '#FF0000'
            }
          },
          typography: {
            primaryFont: 'Russo One',
            fontWeight: 'normal'
          }
        };

        // Add Neon preset for pindrop game
        presets.neon = {
          colors: {
            background: {
              gradient: {
                start: '#0F0C29',
                end: '#24243e'
              }
            },
            cardBackground: '#1a1a2e',
            cardOpacity: 0.9,
            titleText: '#00f5ff',
            subtitleText: '#ff0080',
            buttonBackground: '#ff0080',
            buttonText: '#ffffff',
            headlineText: '#00f5ff',
            subheadlineText: '#ff0080',
            results: {
              cardBackground: '#1a1a2e',
              cardBackgroundOpacity: 0.9,
              scoreLabelText: '#00f5ff',
              scoreValueText: '#ffffff',
              joinButtonGradientFrom: '#ff0080',
              joinButtonGradientTo: '#ff1090',
              joinButtonText: '#ffffff'
            },
            endCard: {
              background: '#1a1a2e',
              backgroundOpacity: 0.9,
              playAgainBackground: '#ff0080',
              playAgainText: '#ffffff',
              shareBackground: '#00f5ff',
              shareText: '#000000',
              ctaButtonBackground: '#ff0080',
              ctaButtonText: '#ffffff',
              ctaButtonOpacity: 1
            },
            leaderboard: {
              background: '#1a1a2e',
              backgroundOpacity: 0.9,
              leaderboardTitleText: '#00f5ff',
              rankText: '#ff0080',
              nameText: '#ffffff',
              scoreText: '#00f5ff',
              userBackground: '#2a2a3e',
              tabPrimary: '#ff0080',
              tabSecondary: '#666699',
              joinButtonGradientFrom: '#ff0080',
              joinButtonGradientTo: '#ff1090',
              joinButtonText: '#ffffff'
            },
            game: {
              topSection: '#0a0a1a',
              textColor: '#00f5ff',
              topSectionOpacity: 0.95,
              polyline: '#ff0080',
              marker: '#ff0080'
            }
          },
          typography: {
            primaryFont: 'Orbitron',
            fontWeight: 'bold'
          }
        };
      }
    }

    return presets;

  } catch (error) {
    return {
      default: DEFAULT_THEME
    };
  }
}; 